/**
 * useLayoutConfig — Core hook that fetches DynamicConfig from the backend.
 *
 * This is the SINGLE source of truth for all UI configuration:
 * - Navigation menu (sidebar categories, items, icons, order)
 * - Dashboard widgets (KPI slugs, colors, layout)
 * - Feature flags (search, notifications, accent switch, etc.)
 * - Theme / branding (logo, primary color, copyright)
 * - Route definitions (dynamic pages beyond /e/:slug)
 *
 * When the API returns no config, a fallback is auto-generated from
 * the Entity Registry — so the UI always works, even without seeded data.
 */

import { useEffect, useState, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { WmsTemplateAssignmentConfig } from '@/lib/wms-template-config';
import {
  applyWarehouseNavigationGovernance,
  type WarehouseNavigationPolicyConfig,
} from '@/lib/warehouse-navigation-governance';
import {
  DYNAMIC_RUNTIME_BINDINGS,
  type DynamicRuntimeBinding,
} from '@/lib/dynamic-runtime-bindings';
import {
  primeRuntimeModuleRegistry,
  resolvedDynamicRuntimeBindingForCategory,
} from '@/lib/runtime-api';
import type { EntityMeta } from '@/types/entity';

const HIDDEN_NAV_ITEM_IDS = new Set(['devops-sync-requests']);
const CORE_VISIBLE_MODULES = new Set(['OMS', 'INBOX', 'RM', 'AI', 'SET']);
// Finance routes are now the canonical module — no longer stripped
// const LEGACY_FINANCE_ROUTE_PREFIXES = ['/finance', '/wms/billing'];

// ─── Types (aligned with @boox/config-contract) ───

export interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  children?: MenuItem[];
  requiredPermission?: string;
  order?: number;
  badge?: { type: 'count' | 'dot'; source?: string };
}

export interface NavigationCategory {
  id: string;
  label: string;
  labelKey?: string;    // i18n key e.g. 'category.trucking'
  icon?: string;
  order: number;
  items: NavigationItem[];
  /**
   * Permission required to VIEW this category.
   * If the user's role/grants do not include this permission the entire category
   * is HIDDEN (not just disabled).
   * Format: '<domain>:<action>'  e.g. 'job:read'
   */
  requiredPermission?: string;
  /**
   * Whether to show this category in horizontal (tab) nav mode.
   * Defaults to true.
   */
  showInTabMode?: boolean;
  /**
   * Sidebar zone assignment.
   * 'core'      — top section (Home, Inbox, Business)
   * 'verticals' — middle section (Transport, Freight, Warehouse)
   * 'support'   — lower section (Finance, Workforce)
   * 'admin'     — bottom-pinned (Settings, Platform)
   */
  zone?: 'core' | 'verticals' | 'support' | 'admin';
  /**
   * Module license required to show this category.
   * e.g. 'TMS', 'FMS', 'WMS', 'ACC', 'OMS', 'INBOX'
   * If the tenant has not enabled this module, the category is hidden.
   */
  requiredModule?: string;
  /**
   * Runtime module/profile binding. When present, menu items for this category
   * are resolved from /dynamic-runtime/navigation instead of frontend item lists.
   */
  dynamicRuntime?: DynamicRuntimeBinding;
}

export interface NavigationItem {
  id: string;
  slug: string;
  label: string;
  labelPlural: string;
  labelKey?: string;
  labelPluralKey?: string;
  icon?: string;
  route: string;
  group?: string;
  order?: number;
  /**
   * Permission required to VIEW this item within its category.
   * If the user's role/grants do not include this permission the item
   * is hidden (the category may still show if other items are visible).
   * Format: '<domain>:<action>'  e.g. 'employee:write', 'hrss:read'
   */
  requiredPermission?: string;
  /**
   * Surfaces where this item should be hidden.
   * e.g. ['pwa', 'extension'] to hide on go-pwa and chrome extension.
   */
  hiddenOnSurface?: string[];
  /**
   * Module license required to show this item.
   * Useful when a category remains visible but individual entries are licensed separately.
   */
  requiredModule?: string;
  /**
   * WMS warehouse profiles where this item should be visible.
   * If undefined → visible in all profiles.
   * e.g. ['ecommerce', 'distribution'] → only visible when tenant uses those profiles.
   */
  wmsProfiles?: string[];
  /**
   * RM profiles where this item should be visible.
   * If undefined -> visible in all RM profiles.
   * Values: b2b_services | product | logistics | freight
   */
  crmProfiles?: string[];
}

export interface KpiWidgetConfig {
  slug: string;
  label: string;
  color: string;       // Tailwind class e.g. 'text-chart-1'
  icon?: string;
  order: number;
}

export interface DashboardWidgetConfig {
  type: 'status-ring' | 'bar-chart' | 'recent-list' | 'entity-query' | 'ai-insight' | 'iframe' | 'custom';
  slug: string;
  title: string;
  limit?: number;
  groupBy?: string;
  order?: number;
  /** For 'custom' type: key into the widget registry */
  widgetKey?: string;
  /** For 'entity-query': server filter expression */
  filter?: Record<string, unknown>;
  /** For 'iframe': source URL */
  src?: string;
  /** For 'ai-insight': prompt template */
  promptTemplate?: string;
  /** Span columns (default 1, max 3) */
  colSpan?: 1 | 2 | 3;
  /** Row span (default 1) */
  rowSpan?: 1 | 2;
}

export interface DashboardConfig {
  kpiWidgets: KpiWidgetConfig[];
  quickNavCategories: string[];   // category IDs to show as quick links
  widgets?: DashboardWidgetConfig[];  // configurable dashboard widgets (Tier 3)
}

export type RealtimeStatusSectionType = 'decision-list' | 'progress-list' | 'unit-stats' | 'feed' | 'outputs';
export type RealtimeStatusTone = 'good' | 'warn' | 'bad' | 'neutral';

export interface RealtimeStatusDetailItemConfig {
  id: string;
  label: string;
  description?: string;
  value?: string | number;
  tone?: RealtimeStatusTone;
  timestamp?: string;
}

export interface RealtimeStatusItemConfig {
  id: string;
  label: string;
  description?: string;
  value?: string | number;
  tone?: RealtimeStatusTone;
  progressPct?: number;
  actionLabel?: string;
  details?: RealtimeStatusDetailItemConfig[];
}

export interface RealtimeStatusSectionConfig {
  id: string;
  title: string;
  type: RealtimeStatusSectionType;
  enabled?: boolean;
  detailLevel?: 'primary' | 'expanded';
  pageSize?: number;
  scope?: {
    dateRangeDays?: number;
    recordLimit?: number;
    unitIds?: string[];
    dataRef?: string;
  };
  visibility?: {
    roleRefs?: string[];
    userWhitelist?: string[];
    userBlacklist?: string[];
    requiredPermission?: string;
  };
  items?: RealtimeStatusItemConfig[];
}

export interface RealtimeStatusTabConfig {
  id: string;
  label: string;
  requiredModule?: string;
  requiredPermission?: string;
  decision?: {
    title: string;
    reason?: string;
  };
  sections: RealtimeStatusSectionConfig[];
}

export interface RealtimeStatusMetricConfig {
  id: string;
  label: string;
  value?: string | number;
  requiredPermission?: string;
}

export interface RealtimeStatusAggregatorConfig {
  enabled: boolean;
  pollMs: number;
  snapshotEndpoint: string;
  title: string;
  setup?: {
    enabled: boolean;
    allowedRoles: string[];
    aiAssistEnabled?: boolean;
  };
  summaryMetrics: RealtimeStatusMetricConfig[];
  tabs: RealtimeStatusTabConfig[];
}

export interface BrandingConfig {
  appName: string;
  appNameKey?: string;  // i18n key
  logo?: string;        // URL or icon name
  logoText?: string;
  heroTitleKey?: string;
  heroDescKey?: string;
  featureKeys?: string[];
  copyright?: string;
}

export interface FeatureFlags {
  searchEnabled: boolean;
  notificationsEnabled: boolean;
  accentSwitchEnabled: boolean;
  themeSwitchEnabled: boolean;
  languageSwitchEnabled: boolean;
  mdiTabsEnabled: boolean;
  commandPaletteEnabled: boolean;
  userMenuItems: UserMenuItem[];
}

export interface UserMenuItem {
  id: string;
  labelKey: string;
  icon: string;
  action: 'profile' | 'settings' | 'shortcuts' | 'signout' | 'custom';
  shortcut?: string;
  dividerBefore?: boolean;
}

export interface AccentOption {
  id: string;
  label: string;
  swatch: string;    // Tailwind class
}

export interface RpaAction {
  id: string;
  label: string;
  icon: string;        // lucide icon name
  action: string;      // RPA action name e.g. 'portnet.screenshot'
  timeout?: number;    // ms, default 30000
}

export interface SurfaceNavigationConfig {
  navigation: NavigationCategory[];
  hideExtraActions?: boolean;
}

export interface NavigationPolicyConfig {
  hiddenCategoryIds?: string[];
  hiddenItemIds?: string[];
  hiddenItemKeys?: string[];
  hiddenByCategory?: Record<string, string[]>;
}

export interface LanguageOption {
  value: string;
  label: string;
}

export interface ThemeOption {
  value: string;
  icon: string;      // icon name
}

export interface LayoutConfig {
  /** Preferred UI locale for the current tenant/surface */
  locale?: string;

  /** Navigation categories with items (drives Sidebar) */
  navigation: NavigationCategory[];

  /** Dashboard widget configuration */
  dashboard: DashboardConfig;

  /** Branding / login page config */
  branding: BrandingConfig;

  /** Feature toggles for UI components */
  features: FeatureFlags;

  /** Accent color options */
  accentOptions: AccentOption[];

  /** Language options */
  languages: LanguageOption[];

  /** Theme options */
  themes: ThemeOption[];

  /** Category order (for sidebar rail) */
  categoryOrder: string[];

  /** Category icon overrides */
  categoryIcons: Record<string, string>;

  /** Notification poll interval in ms */
  notificationPollMs: number;

  /** Whether config came from API vs. fallback */
  isDefault: boolean;

  /**
   * Preferred tenant landing route after login.
   * When set, the root route should redirect here at runtime without redeploy.
   */
  landingRoute?: string;

  /**
   * Navigation layout mode.
   * 'vertical'   — icon-rail sidebar (web default)
   * 'horizontal' — top tab bar (configurable per tenant)
   */
  navMode?: 'vertical' | 'horizontal';

  /**
   * Mobile nav style for go-mobile / go-pwa.
   * 'tabs'   — bottom tab bar (default)
   * 'drawer' — slide-in drawer
   */
  mobileNavStyle?: 'tabs' | 'drawer';

  /**
   * RPA quick-action buttons for Chrome Extension side panel.
   * If not set, extension-shell uses built-in defaults.
   */
  rpaActions?: RpaAction[];

  /**
   * Optional PWA-specific navigation override.
   * When set, go-pwa uses this menu instead of the full tenant navigation.
   */
  pwaNavigation?: SurfaceNavigationConfig;

  /**
   * Optional Chrome extension-specific navigation override.
   * When set, extension-shell uses this menu instead of the full tenant navigation.
   */
  extensionNavigation?: SurfaceNavigationConfig;

  /** Tenant-configured navigation visibility rules. */
  navigationPolicy?: NavigationPolicyConfig;

  /** Use only configured navigation categories; do not append fallback modules. */
  strictNavigation?: boolean;

  /**
   * WMS warehouse profile key.
   * Controls which KPIs, flow steps, and nav items are visible in the WMS module.
    * Values: 'cfs' | 'ecommerce' | 'general' | 'manufacturing' |
    * 'distribution' | 'yard' | 'hybrid'
   * Defaults to 'hybrid' (show everything).
   */
  warehouseProfile?: string;

  /** Optional platform template code that resolves back to a WMS profile. */
  warehouseProfileTemplateCode?: string;

  /** Optional tenant-level assignment rules for multi-template resolution. */
  warehouseTemplateAssignments?: WmsTemplateAssignmentConfig[];

  /**
   * Transitional navigation policy for WM/WG/WP only.
   * CF remains an independent module and is not controlled by this policy.
   */
  warehouseNavigationPolicy?: WarehouseNavigationPolicyConfig;

  /**
   * RM foundation profiles enabled for this tenant.
   * A tenant may combine profiles, e.g. ['product', 'b2b_services'] for
   * Stamford Tyre or ['freight', 'logistics', 'b2b_services'] for forwarders.
   */
  crmProfiles?: string[];

  /** Tenant wording override for RM nav and page labels. */
  crmTerminology?: Partial<{
    account: string;
    contact: string;
    deal: string;
    quote: string;
    signal: string;
    followUp: string;
    recovery: string;
    incentive: string;
  }>;

  /**
   * Top-bar realtime tenant status aggregator.
   * Data is scoped to the current tenant and filtered again by user permission.
   * Tabs and sections are config-driven so tenants can hide Finance, rename Ops,
   * or add vertical-specific sections without frontend code changes.
   */
  realtimeStatusAggregator?: RealtimeStatusAggregatorConfig;
}

// ─── Default values ────────────────────────────────

/**
 * Navigation category order — restructured into logical zones:
 *
 * ── Core (always visible) ──
 *   home       → Dashboard / Shortcuts / Recent
 *   inbox      → Messages + Tasks
 *   business   → Sales / Purchase / Setup / Reports (horizontal layer)
 * ── Verticals (permission-gated) ──
 *   transport  → Container Trucking / Local Transport operations
 *   freight    → Sea/Air Import/Export / CFS / Customs
 *   warehouse  → Receiving / Putaway / Pick/Pack / Dispatch
 * ── Support (permission-gated) ──
 *   finance    → AR / AP / GL / Reports
 *   workforce  → Employees / Branches / Access / Payroll
 * ── Admin (bottom-pinned) ──
 *   settings      → Tenant config
 *   cc-command       → platform command surface
 *   cc-revenue       → revenue inbox / qualification / onboarding intake
 *   cc-delivery      → teamspace / rollout / runtime delivery
 *   cc-ceo           → executive surface
 *   cc-tenant-admin  → tenant governance
 *   cc-systems       → builder / config / deploy / logs
 */
export const DEFAULT_CATEGORY_ORDER = [
  'home',        // Dashboard + Today + Inbox + Network
  'builder',     // Business Builder — group-level owner map + AI Chief of Staff
  'ai',          // AI — agentic native runtime
  'rm',          // BM Business Management — customer, supplier, partner, and service relationships
  // Keep legacy modules visible first while the new dynamic modules sit beside them.
  'transport',   // Local Transport + Delivery
  'haulage',     // Container Haulage (FCL)
  'freight',     // Sea/Air Import/Export + CFS + Customs
  'tm-runtime',  // TM canonical Transport runtime
  'ct',          // CT Container Trucking
  'fleet',       // Fleet Lifecycle Management
  'wp',          // WP Wasco Project Warehouse
  'wg',          // WG 3PL Generic Warehouse
  'wm',          // WM Warehouse
  'cf',          // CF CFS Warehouse
  'fm',          // FM Freight
  'mt',          // MT Maritime
  'ac',          // AC Accounting
  'operational-intelligence',
  'depot',       // Gate / yard / inspection / M&R
  'soc',         // Shipper-owned container control
  'pro2',        // CMS PRO2 read-only browser
  'cargoerp',    // CargoERP Legacy Browser
  'ezship',      // EZShip Legacy Browser
  'sysfreight',  // SysFreight Legacy Browser
  'kdcfs',       // KDCFS Legacy Browser
  'warehouse',   // WMS operations
  'supply-chain', // SCM — Procurement / Sales / Inventory / Costing
  'service',     // SVC — Service Orders / Equipment / Crew
  'finance',     // AR / AP / GL / Reports + Audit
  'workforce',   // Employee / Branch / Access management
  'settings',    // Tenant settings
  // ── CC Admin Console (platform_admin only) ──
  'cc-command',      // Command / platform posture
  'cc-revenue',      // Revenue inbox / qualification
  'cc-delivery',     // Teamspace / rollout / runtime delivery
  'cc-ceo',          // Strategy / fundraising / data room / AI Co-CEO
  'cc-tenant-admin', // Tenant governance / auth / policies
  'cc-systems',      // Builder / config / deploy / logs / analytics
  'cc-ai-runtime',   // AI employees / prompts / autonomous tasks
  'cc-monitoring',   // Platform monitoring / logs / deploy control
];

/**
 * Category icon map (lucide icon names).
 * All icons are DSL-driven; this is just the fallback/default per category.
 */
export const DEFAULT_CATEGORY_ICONS: Record<string, string> = {
  // Core
  home:         'home',
  builder:      'map',
  messaging:    'inbox',
  inbox:        'inbox',    // legacy alias
  business:     'clipboard-list',
  aiw:          'zap',
  ai:           'zap',
  crm:          'user-check',
  rm:           'user-check',
  // Verticals
  transport:    'route',
  haulage:      'container',
  fleet:        'truck',
  freight:      'ship',
  'operational-intelligence': 'radar',
  sas:          'anchor',
  mt:           'anchor',
  liner:        'anchor',
  wp:           'warehouse',
  wg:           'forklift',
  depot:        'building-2',
  soc:          'container',
  pro2:         'database',
  cargoerp:     'database',
  ezship:       'ship',
  sysfreight:   'database',
  kdcfs:        'warehouse',
  warehouse:    'warehouse',
  'supply-chain': 'layers',
  service:        'wrench',
  // Support
  finance:      'banknote',
  audit:        'shield-check',
  workforce:    'users-round',
  network:      'globe',
  // Admin (bottom-pinned)
  settings:     'settings',
  platform:     'zap',          // legacy — kept for backward compat
  'cc-command': 'layout-dashboard',
  'cc-revenue': 'inbox',
  'cc-delivery': 'rocket',
  'cc-ceo': 'briefcase',
  'cc-tenant-admin': 'building-2',
  'cc-systems': 'wrench',
  'cc-ai-runtime': 'cpu',
  'cc-monitoring': 'activity',
  // ── Legacy aliases (backward compat with existing API config rows) ──
  tms:             'route',
  fms:             'ship',
  wms:             'warehouse',
  accounting:      'banknote',
  parties:         'user-check',
  'ai-command':    'zap',
  trucking:        'truck',
  freight2:        'ship',
  accounts:        'banknote',
  // messaging: already defined above with 'inbox' icon — no alias needed
  agent:           'cpu',
  config:          'list-checks',
  transaction:     'briefcase',
  master:          'book-open',
  communication:   'message-square',
  event:           'bell',
  attachment:      'file-text',
};

export const CATEGORY_LABELS: Record<string, string> = {
  pro2: 'PRO2',
  cargoerp: 'CargoERP',
  ezship: 'EZShip',
  sysfreight: 'SysFreight',
  kdcfs: 'KDCFS',
  accounts: 'Accounting',
  builder: 'Business Builder',
  business: 'OM',
  aiw: 'AI',
  ai: 'AI',
  crm: 'BM Business Management',
  rm: 'BM Business Management',
  wp: 'WP',
  wg: 'WG 3PL Warehouse',
  warehouse: 'WMS v1',
  'operational-intelligence': 'Operational Intelligence',
  fleet: 'Fleet',
  sas: 'MT Maritime',
  liner: 'MT Maritime',
  mt: 'MT Maritime',
  depot: 'Depot',
  soc: 'SOC',
  'cc-command': '指挥台',
  'cc-revenue': 'Revenue Ops',
  'cc-delivery': 'Delivery Ops',
  'cc-ceo': 'CEO',
  'cc-tenant-admin': '租户管理',
  'cc-systems': 'SDK 与系统',
  'cc-ai-runtime': 'AI 运行时',
  'cc-monitoring': '监控',
};

export const DEFAULT_REALTIME_STATUS_AGGREGATOR: RealtimeStatusAggregatorConfig = {
  enabled: true,
  pollMs: 15_000,
  snapshotEndpoint: '/status-aggregator/snapshot',
  title: 'Realtime Status',
  setup: {
    enabled: true,
    allowedRoles: ['solution_admin', 'platform_admin', 'admin'],
    aiAssistEnabled: true,
  },
  summaryMetrics: [
    { id: 'pendingWork', label: 'Pending', value: 0 },
    { id: 'ongoingWork', label: 'Ongoing now', value: 0 },
    { id: 'exceptionWork', label: 'Exceptions', value: 0 },
    { id: 'doneToday', label: 'Done today', value: 0 },
    { id: 'doneWtdMtd', label: 'Done WTD / MTD', value: '0 / 0' },
  ],
  tabs: [
    {
      id: 'now',
      label: 'Now',
      decision: {
        title: 'Review the top blocker before the next dispatch wave.',
        reason: 'The panel ranks tenant-scoped work by customer impact, time cutoff, and permission gate.',
      },
      sections: [
        {
          id: 'work-status',
          title: 'Work status',
          type: 'decision-list',
          detailLevel: 'primary',
          pageSize: 4,
          scope: { dateRangeDays: 1, recordLimit: 6 },
          items: [
            { id: 'pending', label: 'Pending', description: 'Waiting action', tone: 'warn', actionLabel: 'List' },
            { id: 'ongoing', label: 'Ongoing', description: 'Running now', tone: 'good', actionLabel: 'Live' },
            { id: 'done-today', label: 'Done today', description: 'Completed today', tone: 'good', actionLabel: 'Done' },
            { id: 'exceptions', label: 'Exceptions', description: 'Failed / blocked / disputed', tone: 'bad', actionLabel: 'Review' },
          ],
        },
        {
          id: 'doing-now',
          title: 'Doing now',
          type: 'feed',
          detailLevel: 'primary',
          pageSize: 6,
          scope: { dateRangeDays: 1, recordLimit: 12, dataRef: 'tenant.realtime.doing_now' },
        },
        {
          id: 'just-done',
          title: 'Just done',
          type: 'feed',
          detailLevel: 'expanded',
          pageSize: 6,
          scope: { dateRangeDays: 1, recordLimit: 12, dataRef: 'tenant.realtime.just_done' },
        },
        {
          id: 'unit-pulse',
          title: 'Tenant unit pulse',
          type: 'unit-stats',
          detailLevel: 'expanded',
          pageSize: 3,
          scope: { dateRangeDays: 1, recordLimit: 6 },
          items: [
            { id: 'transport', label: 'Transport Desk', description: 'Trips / SLA / cutoff risk', value: 'live' },
            { id: 'warehouse', label: 'Warehouse Desk', description: 'Tasks / QC / stock count', value: 'live' },
            { id: 'cs', label: 'Customer Service', description: 'Claims / portal / documents', value: 'live' },
          ],
        },
      ],
    },
    {
      id: 'ai',
      label: 'AI',
      requiredModule: 'AI',
      decision: {
        title: 'Let AI continue healthy runs; review only blocked approvals.',
        reason: 'AI status should surface human intervention points, not every background task.',
      },
      sections: [
        {
          id: 'ai-runs',
          title: 'AI performance',
          type: 'progress-list',
          detailLevel: 'primary',
          pageSize: 3,
          scope: { dateRangeDays: 1, recordLimit: 6, dataRef: 'tenant.ai.runs' },
          items: [
            { id: 'pipelines', label: 'Running pipelines', description: 'Tenant AI workflows', progressPct: 70, tone: 'good' },
            { id: 'approvals', label: 'Pending approvals', description: 'Human review required', progressPct: 45, tone: 'warn' },
            { id: 'failed-runs', label: 'Failed runs', description: 'Need retry or fix hint', progressPct: 20, tone: 'bad' },
          ],
        },
        {
          id: 'ai-performance',
          title: 'AI done / running',
          type: 'progress-list',
          detailLevel: 'primary',
          pageSize: 3,
          scope: { dateRangeDays: 30, recordLimit: 12, dataRef: 'tenant.ai.performance' },
        },
        {
          id: 'ai-feed',
          title: 'AI live list',
          type: 'feed',
          detailLevel: 'expanded',
          pageSize: 5,
          scope: { dateRangeDays: 1, recordLimit: 20, dataRef: 'tenant.ai.feed' },
        },
      ],
    },
    {
      id: 'team',
      label: 'Team',
      decision: {
        title: 'Move capacity to the queue with the oldest blocker.',
        reason: 'Team view is for handoff and overloaded queues, not full HR reporting.',
      },
      sections: [
        {
          id: 'workload',
          title: 'Team workload',
          type: 'progress-list',
          detailLevel: 'primary',
          pageSize: 3,
          scope: { dateRangeDays: 1, recordLimit: 6 },
          items: [
            { id: 'cs', label: 'Customer Service', description: 'Claims and customer updates', progressPct: 64, tone: 'warn' },
            { id: 'ops', label: 'Operations', description: 'Dispatch and exception handling', progressPct: 78, tone: 'warn' },
            { id: 'warehouse', label: 'Warehouse', description: 'Receiving, QC, picking', progressPct: 55, tone: 'neutral' },
          ],
        },
        {
          id: 'human-performance',
          title: 'Human performance',
          type: 'progress-list',
          detailLevel: 'primary',
          pageSize: 3,
          scope: { dateRangeDays: 30, recordLimit: 12, dataRef: 'tenant.human.performance' },
        },
        {
          id: 'approvals',
          title: 'Approvals',
          type: 'feed',
          detailLevel: 'expanded',
          pageSize: 5,
          scope: { dateRangeDays: 7, recordLimit: 20, dataRef: 'tenant.approvals' },
        },
      ],
    },
    {
      id: 'ops',
      label: 'Ops',
      decision: {
        title: 'Clear the shortest cutoff risk before routine queue work.',
        reason: 'Ops is ranked by time cutoff and customer-impacting exceptions.',
      },
      sections: [
        {
          id: 'modules',
          title: 'Ops by module',
          type: 'decision-list',
          detailLevel: 'primary',
          pageSize: 4,
          scope: { dateRangeDays: 1, recordLimit: 8 },
          items: [
            { id: 'transport', label: 'Transport', description: 'Trips, dispatch, cutoff', tone: 'warn', actionLabel: 'Watch' },
            { id: 'warehouse', label: 'Warehouse', description: 'Tasks, QC, stock count', tone: 'good', actionLabel: 'Live' },
            { id: 'freight', label: 'Freight', description: 'BL, docs, schedule', tone: 'neutral', actionLabel: 'Clear' },
          ],
        },
        {
          id: 'exceptions',
          title: 'Exception list',
          type: 'feed',
          detailLevel: 'expanded',
          pageSize: 5,
          scope: { dateRangeDays: 3, recordLimit: 20, dataRef: 'tenant.ops.exceptions' },
        },
      ],
    },
    {
      id: 'finance',
      label: 'Finance',
      requiredModule: 'AC',
      requiredPermission: 'finance.status.read',
      decision: {
        title: 'Hold posting where approval or balance checks are incomplete.',
        reason: 'Finance status is permission-gated and should expose control risk before cash movement.',
      },
      sections: [
        {
          id: 'finance-control',
          title: 'Finance control',
          type: 'progress-list',
          detailLevel: 'primary',
          pageSize: 3,
          scope: { dateRangeDays: 30, recordLimit: 6, dataRef: 'tenant.finance.control' },
          visibility: { requiredPermission: 'finance.status.read' },
          items: [
            { id: 'ar', label: 'AR exposure', description: 'Overdue and disputed entries', progressPct: 62, tone: 'warn' },
            { id: 'ap', label: 'AP pending', description: 'Supplier bills waiting match', progressPct: 44, tone: 'neutral' },
            { id: 'close', label: 'Close readiness', description: 'Checks complete', progressPct: 73, tone: 'good' },
          ],
        },
        {
          id: 'finance-alerts',
          title: 'Finance alerts',
          type: 'feed',
          detailLevel: 'expanded',
          pageSize: 5,
          scope: { dateRangeDays: 30, recordLimit: 20, dataRef: 'tenant.finance.alerts' },
          visibility: { requiredPermission: 'finance.status.read' },
        },
      ],
    },
  ],
};

/**
 * Permission required to see each category.
 * This is the FALLBACK; the actual value comes from NavigationCategory.requiredPermission
 * which can be overridden via the API config DSL.
 */
export const CATEGORY_PERMISSIONS: Record<string, string> = {
  // Core
  home:         '*',                 // everyone sees Home
  builder:      'builder:read',
  inbox:        'conversation:read',
  messaging:    'conversation:read',
  communication:'conversation:read',
  business:     'order:read',
  aiw:          '*',
  ai:           '*',
  crm:          '*',
  rm:           '*',
  // Verticals
  transport:    'job:read',
  fleet:        'job:read',
  freight:      'shipment:read',
  wp:           'wp.read',
  'operational-intelligence': '*',
  sas:          'shipment:read',
  mt:           'shipment:read',
  liner:        'shipment:read',
  depot:        'job:read',
  soc:          'job:read',
  pro2:         'shipment:read',
  cargoerp:     'shipment:read',
  ezship:       'shipment:read',
  sysfreight:   'shipment:read',
  kdcfs:        'shipment:read',
  warehouse:    'warehouse:read',
  'supply-chain': 'supply-chain:read',
  service:        'service-order:read',
  // Support
  finance:      'ledger:read',
  workforce:    '*',
  network:      'conversation:read',
  // Admin (bottom-pinned)
  settings:     'config:read',
  platform:     'platform:admin',   // legacy — kept for backward compat
  // CC Admin Console categories
  'cc-command':      'platform:admin',
  'cc-revenue':      'platform:admin',
  'cc-delivery':     'platform:admin',
  'cc-ceo':          'platform:admin',
  'cc-tenant-admin': 'platform:admin',
  'cc-systems':      'platform:admin',
  // Platform-only entity categories (logs, integrations, devops)
  config:       'platform:admin',
  agent:        'platform:admin',
  event:        'platform:admin',
  // ── Legacy aliases (backward compat) ──
  // NOTE: 'agent' and 'config' intentionally NOT repeated here.
  // They map to 'platform:admin' above (entity categories like activity-log,
  // notifications, devops-servers must stay platform-admin-only).
  tms:          'job:read',
  fms:          'shipment:read',
  wms:          'warehouse:read',
  accounting:   'ledger:read',
  parties:      'customer:read',
  'ai-command': 'agent:read',
  devops:       'devops:read',
  trucking:     'job:read',
  accounts:     'ledger:read',
};

const DEFAULT_KPI_COLORS = ['text-chart-1', 'text-chart-2', 'text-chart-3', 'text-chart-4'];

const DEFAULT_ACCENT_OPTIONS: AccentOption[] = [
  { id: 'blue', label: 'Blue', swatch: 'bg-blue-500' },
  { id: 'rose', label: 'Rose', swatch: 'bg-rose-400' },
  { id: 'pink', label: 'Pink', swatch: 'bg-pink-400' },
  { id: 'purple', label: 'Purple', swatch: 'bg-purple-500' },
  { id: 'teal', label: 'Teal', swatch: 'bg-teal-500' },
  { id: 'green', label: 'Green', swatch: 'bg-green-500' },
  { id: 'amber', label: 'Amber', swatch: 'bg-amber-500' },
  { id: 'indigo', label: 'Indigo', swatch: 'bg-indigo-500' },
];

const DEFAULT_LANGUAGES: LanguageOption[] = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
  { value: 'zh-Hant', label: '繁體中文' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'ms', label: 'Bahasa Melayu' },
  { value: 'ja', label: '日本語' },
  { value: 'th', label: 'ไทย' },
  { value: 'vi', label: 'Tiếng Việt' },
];

const DEFAULT_THEMES: ThemeOption[] = [
  { value: 'light', icon: 'sun' },
  { value: 'dark', icon: 'moon' },
  { value: 'system', icon: 'monitor' },
];

const DEFAULT_USER_MENU: UserMenuItem[] = [
  { id: 'profile', labelKey: 'user.profile', icon: 'user', action: 'profile' },
  { id: 'settings', labelKey: 'user.settings', icon: 'settings', action: 'settings' },
  { id: 'shortcuts', labelKey: 'user.shortcuts', icon: 'command', action: 'shortcuts', shortcut: '⌘K' },
  { id: 'signout', labelKey: 'user.signOut', icon: 'log-out', action: 'signout', dividerBefore: true },
];

const DEFAULT_FEATURES: FeatureFlags = {
  searchEnabled: true,
  notificationsEnabled: true,
  accentSwitchEnabled: true,
  themeSwitchEnabled: true,
  languageSwitchEnabled: true,
  mdiTabsEnabled: true,
  commandPaletteEnabled: true,
  userMenuItems: DEFAULT_USER_MENU,
};

export const DEFAULT_BRANDING: BrandingConfig = {
  appName: 'Boox Platform',
  appNameKey: 'app.name',
  logo: '',
  logoText: 'app.logo',
  heroTitleKey: 'login.heroTitle',
  heroDescKey: 'login.heroDesc',
  featureKeys: ['login.feature1', 'login.feature2', 'login.feature3'],
  copyright: `© ${new Date().getFullYear()} Boox Platform. All rights reserved.`,
};

// ─── Config API response type ──────────────────────

interface ConfigApiResponse {
  config: {
    tenant: string;
    channel: string;
    layoutVersion: string;
    menu: MenuItem[];
    pages: Array<{ id: string; templateId: string; slots?: Record<string, unknown>; dataSources?: string[] }>;
    capabilities: string[];
    featureFlags: Record<string, boolean>;
    signature: string;
    issuedAt: string;
    enabledSkills?: string[];
    theme?: { primaryColor: string; logo?: string; darkMode?: boolean };
    locale?: string;
    // Extended fields stored in config JSON
    navigation?: NavigationCategory[];
    dashboard?: DashboardConfig;
    branding?: BrandingConfig;
    features?: Partial<FeatureFlags>;
    accentOptions?: AccentOption[];
    languages?: LanguageOption[];
    themes?: ThemeOption[];
    categoryOrder?: string[];
    categoryIcons?: Record<string, string>;
    notificationPollMs?: number;
    realtimeStatusAggregator?: RealtimeStatusAggregatorConfig;
    landingRoute?: string;
    navMode?: 'vertical' | 'horizontal';
    mobileNavStyle?: 'tabs' | 'drawer';
    rpaActions?: RpaAction[];
    pwaNavigation?: SurfaceNavigationConfig;
    extensionNavigation?: SurfaceNavigationConfig;
    navigationPolicy?: NavigationPolicyConfig;
    /** When true, use only the API navigation — do not append fallback categories */
    strictNavigation?: boolean;
    /** WMS warehouse profile key (cfs|ecommerce|general|manufacturing|distribution|yard|hybrid) */
    warehouseProfile?: string;
    warehouseProfileTemplateCode?: string;
    warehouseTemplateAssignments?: WmsTemplateAssignmentConfig[];
    warehouseNavigationPolicy?: WarehouseNavigationPolicyConfig;
    /** RM foundation profile keys (b2b_services|product|logistics|freight) */
    crmProfiles?: string[];
    crmTerminology?: LayoutConfig['crmTerminology'];
  };
  fromCache: boolean;
  isDefault: boolean;
}

// ─── Auto-generate fallback from Entity Registry ───

/**
 * Zone assignment for well-known category IDs.
 * Used when API config does not include a `zone` field on categories.
 */
const ZONE_DEFAULTS: Record<string, NavigationCategory['zone']> = {
  home: 'core', builder: 'core', inbox: 'core', messaging: 'core', communication: 'core', business: 'core', aiw: 'core', ai: 'core', crm: 'core', rm: 'core',
  transport: 'verticals', haulage: 'verticals', fleet: 'verticals', freight: 'verticals', wp: 'verticals', 'operational-intelligence': 'support', sas: 'verticals', liner: 'verticals', mt: 'verticals', depot: 'verticals', soc: 'verticals', pro2: 'verticals', cargoerp: 'verticals', ezship: 'verticals', sysfreight: 'verticals', kdcfs: 'verticals', warehouse: 'verticals', 'supply-chain': 'verticals', service: 'verticals',
  tms: 'verticals', fms: 'verticals', wms: 'verticals',
  finance: 'support', audit: 'support', accounts: 'support', workforce: 'support', network: 'support',
  settings: 'admin', platform: 'admin', 'ai-command': 'admin',
  'cc-command': 'core',
  'cc-revenue': 'core',
  'cc-delivery': 'core',
  'cc-ceo': 'core',
  'cc-tenant-admin': 'support',
  'cc-systems': 'admin',
};

/**
 * Module requirement for well-known category IDs.
 * Used when API config does not include a `requiredModule` field.
 */
export const MODULE_DEFAULTS: Record<string, string> = {
  // home: intentionally NO module gate — always visible, the landing hub
  ai: 'AI', aiw: 'AI', aic: 'AI',
  crm: 'BM', rm: 'BM',
  transport: 'TMS', haulage: 'HMS', tms: 'TMS',
  fleet: 'FLM',
  freight: 'FMS', fms: 'FMS',
  wp: 'WP',
  wg: 'WG',
  'operational-intelligence': 'OPI',
  sas: 'MT',
  liner: 'MT',
  mt: 'MT',
  depot: 'DMS',
  soc: 'SOC',
  pro2: 'PRO2',
  cargoerp: 'CERP',
  ezship: 'EZSH',
  sysfreight: 'SFRT',
  kdcfs: 'KDCFS',
  warehouse: 'WMS', wms: 'WMS',
  scm: 'SCM', 'supply-chain': 'SCM',
  svc: 'SVC', service: 'SVC',
  acc: 'AC', ac: 'AC', finance: 'AC', audit: 'AC', accounts: 'AC', settings: 'SET',
  hrm: 'HRM', workforce: 'HRM',
  // Legacy aliases (backward compat — kept so old API configs don't break)
  messaging: 'INBOX', inbox: 'INBOX', business: 'OMS', network: 'OMS',
};

const HIDDEN_DYNAMIC_CATEGORY_IDS = new Set<string>();
const DYNAMIC_CATEGORY_IDS = DYNAMIC_RUNTIME_BINDINGS
  .map((binding) => binding.categoryId)
  .filter((categoryId) => !HIDDEN_DYNAMIC_CATEGORY_IDS.has(categoryId));

function normalizeNavigationCategoryId(categoryId: string): string {
  if (categoryId === 'aiw' || categoryId === 'aic') return 'ai';
  if (categoryId === 'acc') return 'ac';
  if (categoryId === 'crm') return 'rm';
  if (categoryId === 'sas' || categoryId === 'liner' || categoryId === 'shipping-agency' || categoryId === 'maritime-desk') return 'mt';
  return categoryId;
}

function categoryRuntimeBinding(categoryId: string): DynamicRuntimeBinding | undefined {
  return resolvedDynamicRuntimeBindingForCategory(normalizeNavigationCategoryId(categoryId));
}

function categoryOrderFor(categoryId: string, orderMap = new Map(DEFAULT_CATEGORY_ORDER.map((id, i) => [id, i]))): number {
  const normalizedCategoryId = normalizeNavigationCategoryId(categoryId);
  return orderMap.get(normalizedCategoryId) ?? categoryRuntimeBinding(normalizedCategoryId)?.categoryOrder ?? 99;
}

function runtimeAugmentedCategoryOrder(): string[] {
  const ids = DEFAULT_CATEGORY_ORDER.filter((categoryId) => !HIDDEN_DYNAMIC_CATEGORY_IDS.has(categoryId));
  for (const categoryId of DYNAMIC_CATEGORY_IDS) {
    if (!ids.includes(categoryId)) ids.push(categoryId);
  }
  return ids.sort((a, b) => categoryOrderFor(a) - categoryOrderFor(b));
}

function runtimeAugmentedCategoryIcons(): Record<string, string> {
  const icons = { ...DEFAULT_CATEGORY_ICONS };
  for (const binding of DYNAMIC_RUNTIME_BINDINGS) {
    if (HIDDEN_DYNAMIC_CATEGORY_IDS.has(binding.categoryId)) continue;
    if (binding.categoryIcon) icons[binding.categoryId] = binding.categoryIcon;
  }
  return icons;
}

function categoryLabelFor(categoryId: string): string {
  const normalizedCategoryId = normalizeNavigationCategoryId(categoryId);
  if (HIDDEN_DYNAMIC_CATEGORY_IDS.has(normalizedCategoryId)) return '';
  return categoryRuntimeBinding(normalizedCategoryId)?.categoryLabel
    ?? CATEGORY_LABELS[normalizedCategoryId]
    ?? (normalizedCategoryId.charAt(0).toUpperCase() + normalizedCategoryId.slice(1));
}

function categoryIconFor(categoryId: string): string {
  const normalizedCategoryId = normalizeNavigationCategoryId(categoryId);
  if (HIDDEN_DYNAMIC_CATEGORY_IDS.has(normalizedCategoryId)) return 'briefcase';
  return categoryRuntimeBinding(normalizedCategoryId)?.categoryIcon
    ?? DEFAULT_CATEGORY_ICONS[normalizedCategoryId]
    ?? 'briefcase';
}

function categoryPermissionFor(categoryId: string): string | undefined {
  const normalizedCategoryId = normalizeNavigationCategoryId(categoryId);
  if (HIDDEN_DYNAMIC_CATEGORY_IDS.has(normalizedCategoryId)) return undefined;
  return categoryRuntimeBinding(normalizedCategoryId)?.requiredPermission ?? CATEGORY_PERMISSIONS[normalizedCategoryId];
}

function categoryZoneFor(categoryId: string): NavigationCategory['zone'] | undefined {
  const normalizedCategoryId = normalizeNavigationCategoryId(categoryId);
  if (HIDDEN_DYNAMIC_CATEGORY_IDS.has(normalizedCategoryId)) return undefined;
  return categoryRuntimeBinding(normalizedCategoryId)?.categoryZone ?? ZONE_DEFAULTS[normalizedCategoryId];
}

function categoryModuleFor(categoryId: string): string | undefined {
  const normalizedCategoryId = normalizeNavigationCategoryId(categoryId);
  if (HIDDEN_DYNAMIC_CATEGORY_IDS.has(normalizedCategoryId)) return undefined;
  return categoryRuntimeBinding(normalizedCategoryId)?.requiredModule ?? MODULE_DEFAULTS[normalizedCategoryId];
}

/**
 * Module-specific sidebar nav items for categories that have dedicated module pages.
 * These replace entity-derived routes (/e/slug) with module routes (/fm/..., /tms/..., etc.)
 * so the sidebar links directly to purpose-built module pages.
 */
export const MODULE_NAV_ITEMS: Record<string, NavigationItem[]> = {
  // ── Home — unified landing hub: dashboard + inbox + Boox Network ──
  home: [
    // Dashboard & daily operations
    { id: 'home-dashboard', slug: 'dashboard', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/', group: 'dashboard' },
    { id: 'home-today', slug: 'today', label: 'Today', labelPlural: 'Today', icon: 'calendar-check', route: '/today', group: 'dashboard' },
    // Inbox & communication
    { id: 'home-conversations', slug: 'conversations', label: 'Conversations', labelPlural: 'Conversations', icon: 'message-square', route: '/inbox/conversations', group: 'inbox' },
    { id: 'home-messaging', slug: 'messaging', label: 'Messaging', labelPlural: 'Messaging', icon: 'mail', route: '/inbox/messaging', group: 'inbox' },
    { id: 'home-inbox', slug: 'mailbox-inbox', label: 'Inbox', labelPlural: 'Inbox', icon: 'inbox', route: '/ai/inbox?copilot=intake&pipeline=intake-message-triage', group: 'inbox' },
    { id: 'home-outbox', slug: 'mailbox-outbox', label: 'Outbox', labelPlural: 'Outbox', icon: 'send', route: '/inbox/outbox', group: 'inbox' },
    // Boox Network — B2B messaging & discovery
    { id: 'net-directory', slug: 'net-directory', label: 'Directory', labelPlural: 'Directory', icon: 'globe', route: '/network/directory', group: 'boox-network' },
    { id: 'net-connections', slug: 'net-connections', label: 'Connections', labelPlural: 'Connections', icon: 'user-check', route: '/network/connections', group: 'boox-network' },
    { id: 'net-alliances', slug: 'net-alliances', label: 'Alliance', labelPlural: 'Alliances', icon: 'network', route: '/network/alliances', group: 'boox-network' },
    { id: 'net-messages', slug: 'net-messages', label: 'Messages', labelPlural: 'Messages', icon: 'message-square', route: '/network/messages', group: 'boox-network' },
    { id: 'net-channels', slug: 'net-channels', label: 'Channels', labelPlural: 'Channels', icon: 'hash', route: '/network/channels', group: 'boox-network' },
  ],
  builder: [
    { id: 'builder-business-home', slug: 'business-home', label: 'Business Home', labelPlural: 'Business Home', icon: 'layout-dashboard', route: '/builder/business-home', group: 'owner-control', requiredPermission: 'builder:read' },
    { id: 'builder-organization', slug: 'organization', label: 'Organization', labelPlural: 'Organization', icon: 'git-branch', route: '/builder/organization', group: 'owner-control', requiredPermission: 'builder:read' },
    { id: 'builder-world-view', slug: 'world-view', label: 'World View', labelPlural: 'World View', icon: 'map', route: '/builder/world-view', group: 'owner-control', requiredPermission: 'builder:read' },
    { id: 'builder-build-plan', slug: 'build-plan', label: 'Build Plan', labelPlural: 'Build Plan', icon: 'target', route: '/builder/business-home?view=build-plan', group: 'owner-control', requiredPermission: 'builder:read' },
    { id: 'builder-artifacts', slug: 'artifacts', label: 'Artifacts', labelPlural: 'Artifacts', icon: 'file-text', route: '/builder/artifacts', group: 'owner-control', requiredPermission: 'builder:read' },
    { id: 'builder-ai-cos-workbench', slug: 'ai-cos-workbench', label: 'AI CoS Workbench', labelPlural: 'AI CoS Workbench', icon: 'bot', route: '/builder/ai-cos-workbench', group: 'owner-control', requiredPermission: 'builder:read' },
    { id: 'builder-review-queue', slug: 'review-queue', label: 'Review Queue', labelPlural: 'Review Queue', icon: 'shield-check', route: '/builder/review-queue', group: 'owner-control', requiredPermission: 'builder:read' },
  ],
  // ── OM — execution shortcuts only ──
  business: [
    { id: 'oms-schedule', slug: 'daily-schedule', label: 'Daily Schedule', labelPlural: 'Daily Schedule', icon: 'calendar-clock', route: '/oms/schedule', group: 'ops' },
    { id: 'oms-pipeline', slug: 'pipeline', label: 'Pipeline', labelPlural: 'Pipeline', icon: 'git-branch', route: '/oms/pipeline', group: 'crm', requiredPermission: 'pipeline:read' },
  ],
  ai: [
    { id: 'aiw-overview', slug: 'overview', label: 'Overview', labelPlural: 'Overview', icon: 'layout-dashboard', route: '/ai' },
    { id: 'aiw-intake-inbox', slug: 'intake-inbox', label: 'Inbox', labelPlural: 'Inbox', icon: 'inbox', route: '/ai/inbox?copilot=intake&pipeline=intake-message-triage', group: 'Channels' },
    { id: 'aiw-outbox', slug: 'outbox', label: 'Outbox', labelPlural: 'Outbox', icon: 'send', route: '/inbox/outbox', group: 'Channels' },
    { id: 'aiw-billing-proof', slug: 'billing-proof', label: 'Ready to Bill', labelPlural: 'Ready to Bill', icon: 'receipt-text', route: '/ai/work-items?copilot=billing&pipeline=billing-proof-check', group: 'Billing Copilot' },
    { id: 'aiw-billing-rates', slug: 'billing-rates', label: 'Price Check', labelPlural: 'Price Check', icon: 'badge-dollar-sign', route: '/ai/work-items?copilot=billing&pipeline=billing-rate-validation', group: 'Billing Copilot' },
    { id: 'aiw-billing-invoices', slug: 'billing-invoices', label: 'Draft Invoices', labelPlural: 'Draft Invoices', icon: 'file-check-2', route: '/ai/work-items?copilot=billing&pipeline=billing-invoice-draft', group: 'Billing Copilot' },
    { id: 'aiw-billing-soa', slug: 'billing-soa', label: 'Payment Follow-up', labelPlural: 'Payment Follow-up', icon: 'calendar-clock', route: '/ai/work-items?copilot=billing&pipeline=billing-soa-follow-up', group: 'Billing Copilot' },
    { id: 'aiw-audit-process', slug: 'audit-process', label: 'Process Check', labelPlural: 'Process Check', icon: 'shield-check', route: '/ai/work-items?copilot=audit&pipeline=audit-compliance', group: 'Audit Copilot' },
    { id: 'aiw-audit-problems', slug: 'audit-problems', label: 'Problem Cases', labelPlural: 'Problem Cases', icon: 'alert-triangle', route: '/ai/work-items?copilot=audit&pipeline=audit-exception', group: 'Audit Copilot' },
    { id: 'aiw-audit-docs', slug: 'audit-docs', label: 'Missing Documents', labelPlural: 'Missing Documents', icon: 'file-warning', route: '/ai/work-items?copilot=audit&pipeline=audit-attachment-gap', group: 'Audit Copilot' },
    { id: 'aiw-audit-charges', slug: 'audit-charges', label: 'Charge Issues', labelPlural: 'Charge Issues', icon: 'circle-dollar-sign', route: '/ai/work-items?copilot=audit&pipeline=audit-charge-anomaly', group: 'Audit Copilot' },
    { id: 'aiw-ceo-summary', slug: 'ceo-summary', label: 'Today Summary', labelPlural: 'Today Summary', icon: 'layout-dashboard', route: '/ai/work-items?copilot=ceo&pipeline=ceo-daily-brief', group: 'CEO Copilot' },
    { id: 'aiw-ceo-margin', slug: 'ceo-margin', label: 'Profit Watch', labelPlural: 'Profit Watch', icon: 'trending-up', route: '/ai/work-items?copilot=ceo&pipeline=ceo-margin-watch', group: 'CEO Copilot' },
    { id: 'aiw-ceo-delay', slug: 'ceo-delay', label: 'Delay Watch', labelPlural: 'Delay Watch', icon: 'timer', route: '/ai/work-items?copilot=ceo&pipeline=ceo-bottleneck', group: 'CEO Copilot' },
    { id: 'aiw-ceo-risk', slug: 'ceo-risk', label: 'Customer Risk', labelPlural: 'Customer Risk', icon: 'user-round-x', route: '/ai/work-items?copilot=ceo&pipeline=ceo-customer-risk', group: 'CEO Copilot' },
    { id: 'aiw-team', slug: 'ai-team', label: 'AI Activity', labelPlural: 'AI Activity', icon: 'activity', route: '/ai/workflows?tab=team', group: 'admin' },
    { id: 'aiw-setup', slug: 'setup', label: 'Setup', labelPlural: 'Setup', icon: 'settings-2', route: '/ai/agents?tab=deploy', group: 'admin', requiredPermission: 'config:read' },
  ],
  wp: [
    { id: 'wp-overview', slug: 'overview', label: 'Overview', labelPlural: 'Overview', icon: 'layout-dashboard', route: '/wp', group: 'overview', requiredPermission: 'wp.read' },
    { id: 'wp-messages', slug: 'messages', label: 'Messages', labelPlural: 'Messages', icon: 'mail', route: '/wp/messages', group: 'overview', requiredPermission: 'wp.read' },
    { id: 'wp-pending-approvals', slug: 'pending-approvals', label: 'Approvals', labelPlural: 'Approvals', icon: 'shield-alert', route: '/wp/pending-approvals', group: 'overview', requiredPermission: 'wp.read' },
    { id: 'wp-po-upload', slug: 'po-upload', label: 'PO Upload', labelPlural: 'PO Upload', icon: 'file-spreadsheet', route: '/wp/purchase-orders', group: 'inbound', requiredPermission: 'wp.read' },
    { id: 'wp-inbound', slug: 'inbound', label: 'Inbound', labelPlural: 'Inbound', icon: 'arrow-down-left', route: '/wp/inbound', group: 'inbound', requiredPermission: 'wp.read' },
    { id: 'wp-qc', slug: 'qc', label: 'QC', labelPlural: 'QC', icon: 'clipboard-check', route: '/wp/inspection', group: 'qc', requiredPermission: 'wp.read' },
    { id: 'wp-inventory', slug: 'inventory', label: 'Inventory', labelPlural: 'Inventory', icon: 'boxes', route: '/wp/inventory', group: 'stock', requiredPermission: 'wp.read' },
    { id: 'wp-stock-ledger', slug: 'stock-ledger', label: 'Stock Ledger', labelPlural: 'Stock Ledger', icon: 'book-open-text', route: '/wp/stock-ledger', group: 'stock', requiredPermission: 'wp.read' },
    { id: 'wp-material-requests', slug: 'material-requests', label: 'Material Requests', labelPlural: 'Material Requests', icon: 'clipboard-list', route: '/wp/material-requests', group: 'outbound', requiredPermission: 'wp.read' },
    { id: 'wp-issue-orders', slug: 'issue-orders', label: 'Outbound', labelPlural: 'Outbound', icon: 'clipboard-check', route: '/wp/outbound?outboundTab=outbound', group: 'outbound', requiredPermission: 'wp.read' },
    { id: 'wp-return-form', slug: 'return-form', label: 'Material Return', labelPlural: 'Material Return', icon: 'rotate-ccw', route: '/wp/returns/form', group: 'return', requiredPermission: 'wp.read' },
    { id: 'wp-return-grn', slug: 'return-grn', label: 'Inventory Return', labelPlural: 'Inventory Return', icon: 'clipboard-plus', route: '/wp/returns/grn', group: 'return', requiredPermission: 'wp.read' },
    { id: 'wp-return-project', slug: 'return-from-project', label: 'Inventory Transfer', labelPlural: 'Inventory Transfer', icon: 'undo-2', route: '/wp/returns/project', group: 'return', requiredPermission: 'wp.read' },
    { id: 'wp-cycle-count', slug: 'cycle-count', label: 'Stock Count', labelPlural: 'Stock Count', icon: 'clipboard-list', route: '/wp/stock-take', group: 'control', requiredPermission: 'wp.read' },
    { id: 'wp-audit-log', slug: 'audit-log', label: 'Audit Log', labelPlural: 'Audit Log', icon: 'history', route: '/wp/audit-log', group: 'control', requiredPermission: 'wp.read' },
    { id: 'wp-inventory-report', slug: 'inventory-report', label: 'Inventory Report', labelPlural: 'Inventory Report', icon: 'file-spreadsheet', route: '/wp/reports', group: 'control', requiredPermission: 'wp.read' },
    { id: 'wp-master-data', slug: 'master-data', label: 'Master Data', labelPlural: 'Master Data', icon: 'package', route: '/wp/master-data', group: 'setup', requiredPermission: 'wp.read' },
    { id: 'wp-warehouse-setup', slug: 'warehouse-setup', label: 'Warehouse Setup', labelPlural: 'Warehouse Setup', icon: 'map-pin', route: '/wp/warehouse-setup', group: 'setup', requiredPermission: 'wp.read' },
  ],
  // Dynamic-runtime first-class categories. Their submenu comes from the runtime
  // navigation graph, while these empty buckets keep distinct new-module icons in
  // the sidebar next to the legacy modules.
  ct: [],
  wg: [],
  cf: [],
  // ── BM — customer workspace + partners / RFQ ──
  rm: [
    { id: 'crm-signals', slug: 'signals', label: 'Leads', labelPlural: 'Leads', icon: 'radar', route: '/rm/signals', group: 'customer', requiredPermission: 'pipeline:read' },
    { id: 'oms-schedule', slug: 'daily-schedule', label: 'Daily Schedule', labelPlural: 'Daily Schedule', icon: 'calendar-clock', route: '/oms/schedule', group: 'operations' },
    { id: 'crm-pipeline', slug: 'pipeline', label: 'Pipeline', labelPlural: 'Pipeline', icon: 'git-branch', route: '/rm/pipeline', group: 'customer', requiredPermission: 'pipeline:read' },
    { id: 'crm-quotations', slug: 'quotations', label: 'Customer Quotation', labelPlural: 'Customer Quotation', icon: 'file-text', route: '/rm/quotations', group: 'customer', requiredPermission: 'pipeline:read' },
    { id: 'crm-follow-ups', slug: 'follow-ups', label: 'Follow-ups', labelPlural: 'Follow-ups', icon: 'calendar-clock', route: '/rm/follow-ups', group: 'customer', requiredPermission: 'pipeline:read' },
    { id: 'crm-recovery', slug: 'recovery', label: 'Recovery', labelPlural: 'Recovery', icon: 'life-buoy', route: '/rm/recovery', group: 'customer', requiredPermission: 'pipeline:read' },
    { id: 'crm-accounts', slug: 'accounts', label: 'Customers', labelPlural: 'Customers', icon: 'building-2', route: '/rm/accounts', group: 'customer', requiredPermission: 'customer:read' },
    { id: 'crm-contacts', slug: 'contacts', label: 'Contacts', labelPlural: 'Contacts', icon: 'contact-round', route: '/rm/contacts', group: 'customer', requiredPermission: 'customer:read' },
    { id: 'oms-parties', slug: 'business-parties', label: 'Business Parties', labelPlural: 'Business Parties', icon: 'building-2', route: '/e/business-parties', group: 'relationship' },
    { id: 'oms-customers', slug: 'customers', label: 'Customers', labelPlural: 'Customers', icon: 'users', route: '/e/customers', group: 'relationship' },
    { id: 'rm-suppliers', slug: 'suppliers', label: 'Suppliers', labelPlural: 'Suppliers', icon: 'factory', route: '/rm/suppliers', group: 'supplier' },
    { id: 'rm-rate-cards', slug: 'rate-cards', label: 'Supplier Quotation', labelPlural: 'Supplier Quotation', icon: 'badge-dollar-sign', route: '/rm/rate-cards', group: 'supplier' },
    { id: 'rm-partners', slug: 'partners', label: 'Partner Directory', labelPlural: 'Partner Directory', icon: 'globe', route: '/rm/partners', group: 'partners' },
    { id: 'rm-connections', slug: 'connections', label: 'Partner Connections', labelPlural: 'Partner Connections', icon: 'user-check', route: '/rm/connections', group: 'partners' },
    { id: 'rm-vendor-panels', slug: 'vendor-panels', label: 'Vendor Panels', labelPlural: 'Vendor Panels', icon: 'users-round', route: '/rm/vendor-panels', group: 'partners' },
    { id: 'oms-circles', slug: 'circles', label: 'Circles', labelPlural: 'Circles', icon: 'users-round', route: '/network/circles', group: 'network' },
    { id: 'oms-groups', slug: 'collab-group', label: 'Groups', labelPlural: 'Groups', icon: 'network', route: '/network/group', group: 'network' },
    { id: 'oms-rfq', slug: 'rfq', label: 'RFQ', labelPlural: 'RFQ', icon: 'file-search', route: '/network/rfq', group: 'sourcing' },
    { id: 'rm-supplier-rfq', slug: 'supplier-rfq', label: 'Supplier RFQ', labelPlural: 'Supplier RFQ', icon: 'file-search', route: '/rm/supplier-rfq', group: 'supplier' },
    { id: 'crm-incentives', slug: 'incentives', label: 'Incentives', labelPlural: 'Incentives', icon: 'dollar-sign', route: '/rm/incentives', group: 'revenue-ops', requiredPermission: 'pipeline:read' },
    { id: 'crm-erp-bridge', slug: 'erp-bridge', label: 'ERP Bridge', labelPlural: 'ERP Bridge', icon: 'plug-zap', route: '/rm/erp-bridge', group: 'integration', requiredPermission: 'pipeline:read' },
  ],
  freight: [
    // ── Overall ──
    { id: 'fms-dashboard', slug: 'fms', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/fm', group: 'overall' },
    { id: 'fms-shipments', slug: 'shipments', label: 'Shipment', labelPlural: 'Shipments', icon: 'file-text', route: '/fm/shipments', group: 'overall' },
    { id: 'fms-containers', slug: 'containers', label: 'Container', labelPlural: 'Containers', icon: 'package', route: '/fm/shipments', group: 'overall' },
    { id: 'fms-cargo', slug: 'shipment-cargos', label: 'Cargo', labelPlural: 'Cargo', icon: 'package', route: '/fm/shipments', group: 'overall' },
    { id: 'fms-setup', slug: 'setup', label: 'Setup', labelPlural: 'Setup', icon: 'settings-2', route: '/fm/master-data', group: 'overall' },
    { id: 'fms-documents', slug: 'documents', label: 'Documents', labelPlural: 'Documents', icon: 'file-text', route: '/fm/documents', group: 'overall' },
    // ── Category ──
    { id: 'fms-import', slug: 'ocean-import', label: 'Import', labelPlural: 'Import', icon: 'arrow-down-left', route: '/fm/import', group: 'category' },
    { id: 'fms-export', slug: 'ocean-export', label: 'Export', labelPlural: 'Export', icon: 'arrow-up-right', route: '/fm/export', group: 'category' },
    { id: 'fms-transhipment', slug: 'transhipment', label: 'Transhipment', labelPlural: 'Transhipment', icon: 'ship', route: '/fm/transhipment', group: 'category' },
    { id: 'fms-cross-trade', slug: 'cross-trade', label: 'Cross Trade', labelPlural: 'Cross Trade', icon: 'activity', route: '/fm/cross-trade', group: 'category' },
    { id: 'fms-local', slug: 'local-handling', label: 'Local', labelPlural: 'Local', icon: 'wrench', route: '/fm/local-handling', group: 'category' },
    { id: 'fms-consol', slug: 'consol', label: 'Consol', labelPlural: 'Consol', icon: 'package', route: '/fm/consols', group: 'category' },
    { id: 'fms-air', slug: 'air', label: 'Air', labelPlural: 'Air', icon: 'plane', route: '/fm/air', group: 'category' },
    // ── Reports (consolidated into Reports Hub) ──
    { id: 'fms-reports-hub', slug: 'reports-hub', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/reports?tab=operations', group: 'reports' },
    // ── Schedule ──
    { id: 'fms-sea-schedule', slug: 'schedule', label: 'Sea Schedule', labelPlural: 'Sea Schedule', icon: 'calendar-days', route: '/fm/schedule', group: 'schedule' },
  ],
  mt: [
    { id: 'sas-dashboard', slug: 'sas-dashboard', label: 'Overview', labelPlural: 'Overview', icon: 'layout-dashboard', route: '/mt', group: 'core' },
    { id: 'sas-port-calls', slug: 'sas-port-calls', label: 'Port Calls', labelPlural: 'Port Calls', icon: 'anchor', route: '/mt/port-calls', group: 'core' },
    { id: 'sas-master-data', slug: 'sas-master-data', label: 'Master Data', labelPlural: 'Master Data', icon: 'database', route: '/mt/master-data', group: 'config' },
    { id: 'sas-accounts', slug: 'sas-accounts', label: 'Agency Accounts', labelPlural: 'Agency Accounts', icon: 'building-2', route: '/mt/accounts', group: 'core' },
    { id: 'sas-commercial', slug: 'sas-commercial', label: 'Commercial', labelPlural: 'Commercial', icon: 'briefcase', route: '/mt/commercial', group: 'finance' },
    { id: 'sas-billing', slug: 'sas-billing', label: 'Billing', labelPlural: 'Billing', icon: 'credit-card', route: '/mt/billing', group: 'finance' },
    { id: 'sas-bookings', slug: 'sas-bookings', label: 'Agency Jobs', labelPlural: 'Agency Jobs', icon: 'ship', route: '/mt/bookings', group: 'core' },
    { id: 'sas-documentation', slug: 'sas-documentation', label: 'Port Call Docs', labelPlural: 'Port Call Docs', icon: 'file-text', route: '/mt/documentation', group: 'execution' },
    { id: 'sas-intake', slug: 'sas-intake', label: 'AI Intake', labelPlural: 'AI Intake', icon: 'mail', route: '/mt/intake', group: 'integration' },
    { id: 'sas-release', slug: 'sas-release', label: 'Release', labelPlural: 'Release', icon: 'shield-check', route: '/mt/releases', group: 'execution' },
    { id: 'sas-local-services', slug: 'sas-local-services', label: 'Local Services', labelPlural: 'Local Services', icon: 'truck', route: '/mt/local-services', group: 'execution' },
    { id: 'sas-tally-control', slug: 'sas-tally-control', label: 'Tally Control', labelPlural: 'Tally Control', icon: 'clipboard-list', route: '/mt/tally', group: 'execution' },
    { id: 'sas-schedules', slug: 'sas-schedules', label: 'Schedules', labelPlural: 'Schedules', icon: 'calendar-clock', route: '/mt/schedule', group: 'planning' },
    { id: 'sas-principal-tasks', slug: 'sas-principal-tasks', label: 'Principal Tasks', labelPlural: 'Principal Tasks', icon: 'bot', route: '/mt/principal-tasks', group: 'integration' },
    { id: 'sas-settings', slug: 'sas-settings', label: 'Settings', labelPlural: 'Settings', icon: 'settings-2', route: '/mt/settings', group: 'config' },
  ],
  depot: [
    { id: 'depot-dashboard', slug: 'depot-dashboard', label: 'Overview', labelPlural: 'Overview', icon: 'layout-dashboard', route: '/depot', group: 'core' },
    { id: 'depot-containers', slug: 'depot-containers', label: 'Containers', labelPlural: 'Containers', icon: 'container', route: '/depot/containers', group: 'execution' },
    { id: 'depot-gate-bookings', slug: 'depot-gate-bookings', label: 'Gate & Bookings', labelPlural: 'Gate & Bookings', icon: 'truck', route: '/depot/gate', group: 'core' },
    { id: 'depot-yard-ops', slug: 'depot-yard-ops', label: 'Yard Ops', labelPlural: 'Yard Ops', icon: 'map-pinned', route: '/depot/yard', group: 'execution' },
    { id: 'depot-maintenance', slug: 'depot-maintenance', label: 'M&R', labelPlural: 'M&R', icon: 'wrench', route: '/depot/maintenance', group: 'control' },
    { id: 'depot-commercial', slug: 'depot-commercial', label: 'Commercial', labelPlural: 'Commercial', icon: 'credit-card', route: '/depot/billing', group: 'finance' },
    { id: 'depot-accounts', slug: 'depot-accounts', label: 'Accounts', labelPlural: 'Accounts', icon: 'building-2', route: '/depot/accounts', group: 'core' },
    { id: 'depot-external-access', slug: 'depot-external-access', label: 'External Access', labelPlural: 'External Access', icon: 'shield-check', route: '/depot/external-access', group: 'control' },
    { id: 'depot-reports', slug: 'depot-reports', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/depot/reports', group: 'reports' },
    { id: 'depot-setup', slug: 'depot-setup', label: 'Setup', labelPlural: 'Setup', icon: 'settings-2', route: '/depot/setup', group: 'config' },
  ],
  soc: [
    { id: 'soc-dashboard', slug: 'soc-dashboard', label: 'Overview', labelPlural: 'Overview', icon: 'layout-dashboard', route: '/soc', group: 'core' },
    { id: 'soc-commercial', slug: 'soc-commercial', label: 'Commercial', labelPlural: 'Commercial', icon: 'briefcase', route: '/soc/commercial', group: 'core' },
    { id: 'soc-contracts', slug: 'soc-contracts', label: 'Contracts', labelPlural: 'Contracts', icon: 'file-text', route: '/soc/contracts', group: 'core' },
    { id: 'soc-revenue', slug: 'soc-revenue', label: 'Revenue', labelPlural: 'Revenue', icon: 'banknote', route: '/soc/revenue', group: 'finance' },
    { id: 'soc-pipeline', slug: 'soc-pipeline', label: 'Pipeline', labelPlural: 'Pipeline', icon: 'git-branch', route: '/soc/pipeline', group: 'core' },
    { id: 'soc-equipment', slug: 'soc-equipment', label: 'Equipment', labelPlural: 'Equipment', icon: 'container', route: '/soc/equipment', group: 'core' },
    { id: 'soc-releases', slug: 'soc-releases', label: 'Releases', labelPlural: 'Releases', icon: 'shield-check', route: '/soc/releases', group: 'execution' },
    { id: 'soc-exceptions', slug: 'soc-exceptions', label: 'Exceptions', labelPlural: 'Exceptions', icon: 'activity', route: '/soc/exceptions', group: 'control' },
    { id: 'soc-repairs', slug: 'soc-repairs', label: 'Repairs', labelPlural: 'Repairs', icon: 'wrench', route: '/soc/repairs', group: 'control' },
    { id: 'soc-billing', slug: 'soc-billing', label: 'Billing', labelPlural: 'Billing', icon: 'credit-card', route: '/soc/billing', group: 'finance' },
  ],
  pro2: [
    { id: 'pro2-import-shipments', slug: 'pro2-import-shipments', label: 'Import Shipment', labelPlural: 'Import Shipments', labelKey: 'nav.pro2.importShipment', labelPluralKey: 'nav.pro2.importShipments', icon: 'ship', route: '/pro2/import/shipments', group: 'import' },
    { id: 'pro2-import-containers', slug: 'pro2-import-containers', label: 'Import Container', labelPlural: 'Import Containers', labelKey: 'nav.pro2.importContainer', labelPluralKey: 'nav.pro2.importContainers', icon: 'package', route: '/pro2/import/containers', group: 'import' },
    { id: 'pro2-import-cargo', slug: 'pro2-import-cargo', label: 'Import Cargo', labelPlural: 'Import Cargo', labelKey: 'nav.pro2.importCargo', labelPluralKey: 'nav.pro2.importCargo', icon: 'box', route: '/pro2/import/cargo', group: 'import' },
    { id: 'pro2-export-shipments', slug: 'pro2-export-shipments', label: 'Export Shipment', labelPlural: 'Export Shipments', labelKey: 'nav.pro2.exportShipment', labelPluralKey: 'nav.pro2.exportShipments', icon: 'ship', route: '/pro2/export/shipments', group: 'export' },
    { id: 'pro2-export-bookings', slug: 'pro2-export-bookings', label: 'Export Booking', labelPlural: 'Export Bookings', labelKey: 'nav.pro2.exportBooking', labelPluralKey: 'nav.pro2.exportBookings', icon: 'clipboard-list', route: '/pro2/export/bookings', group: 'export' },
    { id: 'pro2-export-containers', slug: 'pro2-export-containers', label: 'Export Container', labelPlural: 'Export Containers', labelKey: 'nav.pro2.exportContainer', labelPluralKey: 'nav.pro2.exportContainers', icon: 'package', route: '/pro2/export/containers', group: 'export' },
    { id: 'pro2-export-cargo', slug: 'pro2-export-cargo', label: 'Export Cargo', labelPlural: 'Export Cargo', labelKey: 'nav.pro2.exportCargo', labelPluralKey: 'nav.pro2.exportCargo', icon: 'box', route: '/pro2/export/cargo', group: 'export' },
    { id: 'pro2-fin-documents', slug: 'pro2-fin-documents', label: 'Invoice / Cost Note', labelPlural: 'Invoice / Cost Notes', labelKey: 'nav.pro2.invCostNote', labelPluralKey: 'nav.pro2.invCostNotes', icon: 'receipt', route: '/pro2/finance/documents', group: 'finance' },
    { id: 'pro2-fin-charges', slug: 'pro2-fin-charges', label: 'Charge Line', labelPlural: 'Charge Lines', labelKey: 'nav.pro2.chargeLine', labelPluralKey: 'nav.pro2.chargeLines', icon: 'list', route: '/pro2/finance/charges', group: 'finance' },
    { id: 'pro2-master-companies', slug: 'pro2-master-companies', label: 'Company', labelPlural: 'Companies', labelKey: 'nav.pro2.company', labelPluralKey: 'nav.pro2.companies', icon: 'building-2', route: '/pro2/master/companies', group: 'master' },
  ],
  cargoerp: [
    { id: 'cargoerp-dashboard', slug: 'cargoerp', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/cargoerp', group: 'core' },
    { id: 'cargoerp-jobs', slug: 'cargoerp-jobs', label: 'Jobs', labelPlural: 'Jobs', icon: 'file-text', route: '/cargoerp/jobs', group: 'core' },
    { id: 'cargoerp-customers', slug: 'cargoerp-customers', label: 'Customers', labelPlural: 'Customers', icon: 'users', route: '/cargoerp/customers', group: 'master' },
  ],
  ezship: [
    { id: 'ezship-dashboard', slug: 'ezship', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/ezship', group: 'core' },
    { id: 'ezship-shipments', slug: 'ezship-shipments', label: 'Shipments', labelPlural: 'Shipments', icon: 'ship', route: '/ezship/shipments', group: 'core' },
    { id: 'ezship-documents', slug: 'ezship-documents', label: 'Documents', labelPlural: 'Documents', icon: 'file-text', route: '/ezship/documents', group: 'core' },
  ],
  sysfreight: [
    { id: 'sysfreight-dashboard', slug: 'sysfreight', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/sysfreight', group: 'core' },
    { id: 'sysfreight-jobs', slug: 'sysfreight-jobs', label: 'Jobs', labelPlural: 'Jobs', icon: 'file-text', route: '/sysfreight/jobs', group: 'core' },
    { id: 'sysfreight-finance', slug: 'sysfreight-finance', label: 'Finance', labelPlural: 'Finance', icon: 'banknote', route: '/sysfreight/finance', group: 'finance' },
  ],
  kdcfs: [
    { id: 'kdcfs-dashboard', slug: 'kdcfs', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/kdcfs', group: 'core' },
    { id: 'kdcfs-jobs', slug: 'kdcfs-jobs', label: 'Jobs', labelPlural: 'Jobs', icon: 'file-text', route: '/kdcfs/jobs', group: 'core' },
    { id: 'kdcfs-containers', slug: 'kdcfs-containers', label: 'Containers', labelPlural: 'Containers', icon: 'package', route: '/kdcfs/containers', group: 'core' },
  ],
  transport: [
    // ── Dashboard ──
    { id: 'tms-dashboard', slug: 'tms', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/tms', group: 'dashboard' },
    // ── Orders ──
    { id: 'tms-jobs', slug: 'jobs', label: 'Job Order', labelPlural: 'Job Orders', icon: 'file-text', route: '/tms/jobs', group: 'orders' },
    { id: 'tms-trips', slug: 'trips', label: 'Trips', labelPlural: 'Trips', icon: 'map-pin', route: '/tms/trips', group: 'orders' },
    { id: 'tms-event-log', slug: 'event-log', label: 'Event Log', labelPlural: 'Event Log', icon: 'activity', route: '/tms/event-log', group: 'orders' },
    // ── Operations ──
    { id: 'tms-planner', slug: 'planner', label: 'Planner', labelPlural: 'Planner', icon: 'calendar', route: '/tms/planner', group: 'operations' },
    { id: 'tms-dispatch', slug: 'dispatch', label: 'Dispatch', labelPlural: 'Dispatch', icon: 'clipboard-list', route: '/tms/dispatch', group: 'operations' },
    { id: 'tms-outbox', slug: 'outbox', label: 'Outbox', labelPlural: 'Outbox', icon: 'send', route: '/tms/outbox', group: 'reports' },
    { id: 'tms-driver-log', slug: 'driver-log', label: 'Driver Log', labelPlural: 'Driver Log', icon: 'user-check', route: '/tms/driver-log', group: 'operations' },
    // ── Finance ──
    { id: 'tms-pricing', slug: 'pricing', label: 'Pricing', labelPlural: 'Pricing', icon: 'dollar-sign', route: '/tms/pricing', group: 'finance' },
    { id: 'tms-settlement', slug: 'settlement', label: 'Settlement', labelPlural: 'Settlement', icon: 'receipt', route: '/tms/settlement', group: 'finance' },
    // ── Reports (consolidated into Reports Hub) ──
    { id: 'tms-reports-hub', slug: 'reports-hub', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/reports?tab=operations', group: 'reports' },
    // ── Docs ──
    { id: 'tms-documents', slug: 'tms-documents', label: 'Documents', labelPlural: 'Documents', icon: 'file-text', route: '/tms/documents', group: 'docs' },
    // ── Config ──
    { id: 'tms-master-data', slug: 'master-data', label: 'Master Data', labelPlural: 'Master Data', icon: 'database', route: '/tms/master-data', group: 'config' },
    { id: 'tms-setup', slug: 'setup', label: 'Setup', labelPlural: 'Setup', icon: 'settings-2', route: '/tms/setup', group: 'config' },
  ],
  fleet: [
    { id: 'flm-dashboard', slug: 'flm', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/flm', group: 'dashboard' },
    { id: 'flm-assets', slug: 'assets', label: 'Vehicle', labelPlural: 'Vehicles', icon: 'truck', route: '/flm/assets', group: 'fleet' },
    { id: 'flm-workshop', slug: 'work-orders', label: 'Work Orders', labelPlural: 'Work Orders', icon: 'wrench', route: '/flm/work-orders', group: 'workshop' },
    { id: 'flm-parts', slug: 'parts', label: 'Part', labelPlural: 'Parts', icon: 'package', route: '/flm/parts', group: 'inventory' },
    { id: 'flm-tyres', slug: 'tyres', label: 'Tyre', labelPlural: 'Tyres', icon: 'circle-dot', route: '/flm/tyres', group: 'inventory' },
    { id: 'flm-costing', slug: 'costing', label: 'Costing', labelPlural: 'Costing', icon: 'calculator', route: '/flm/costing', group: 'finance' },
    { id: 'flm-reports', slug: 'reports', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/flm/reports', group: 'reports' },
  ],
  haulage: [
    // ── Dashboard ──
    { id: 'hms-dashboard', slug: 'hms', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/hms', group: 'dashboard' },
    { id: 'hms-control-room', slug: 'control-room', label: 'Control Room', labelPlural: 'Control Room', icon: 'activity', route: '/hms/control-room', group: 'dashboard' },
    // ── Orders ──
    { id: 'hms-jobs', slug: 'jobs', label: 'Job Order', labelPlural: 'Job Orders', icon: 'file-text', route: '/hms/jobs', group: 'orders' },
    { id: 'hms-containers', slug: 'containers', label: 'Container', labelPlural: 'Containers', icon: 'package', route: '/hms/containers', group: 'orders' },
    { id: 'hms-trips', slug: 'trips', label: 'Trips', labelPlural: 'Trips', icon: 'map-pin', route: '/hms/trips', group: 'orders' },
    { id: 'hms-pricing', slug: 'pricing', label: 'Pricing', labelPlural: 'Pricing', icon: 'dollar-sign', route: '/hms/pricing', group: 'orders' },
    { id: 'hms-event-log', slug: 'event-log', label: 'Event Log', labelPlural: 'Event Log', icon: 'activity', route: '/hms/event-log', group: 'orders' },
    { id: 'hms-documents', slug: 'hms-documents', label: 'Documents', labelPlural: 'Documents', icon: 'file-text', route: '/hms/documents', group: 'integration' },
    { id: 'hms-matchbox-reuse', slug: 'matchbox-reuse', label: 'Re-use Leads', labelPlural: 'Re-use Leads', icon: 'refresh-cw', route: '/hms/matchbox-reuse', group: 'integration' },
    // ── Operations ──
    { id: 'hms-planner', slug: 'planner', label: 'Planner', labelPlural: 'Planner', icon: 'calendar', route: '/hms/planner', group: 'operations' },
    { id: 'hms-dispatch', slug: 'dispatch', label: 'Dispatch', labelPlural: 'Dispatch', icon: 'clipboard-list', route: '/hms/dispatch', group: 'operations' },
    { id: 'hms-driver-log', slug: 'driver-log', label: 'Driver Log', labelPlural: 'Driver Log', icon: 'user-check', route: '/hms/driver-log', group: 'operations' },
    // ── Reports (consolidated into Reports Hub) ──
    { id: 'hms-reports-hub', slug: 'reports-hub', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/reports?tab=operations', group: 'reports' },
    // ── AI ──
    { id: 'hms-ai-cs-manager', slug: 'ai-cs-manager', label: 'AI CS Manager', labelPlural: 'AI CS Manager', icon: 'bot', route: '/hms/ai-cs-manager', group: 'ai' },
    { id: 'hms-staff', slug: 'staff', label: 'Staff Performance', labelPlural: 'Staff Performance', icon: 'users', route: '/hms/staff', group: 'ai' },
    // ── Config ──
    { id: 'hms-setup', slug: 'setup', label: 'Setup', labelPlural: 'Setup', icon: 'settings-2', route: '/hms/setup', group: 'config' },
  ],
  settings: [
    { id: 'settings-company', slug: 'company', label: 'Company', labelPlural: 'Company Settings', icon: 'building-2', route: '/settings/company', group: 'tenant' },
    { id: 'settings-page-performance', slug: 'page-performance', label: 'Page Performance', labelPlural: 'Page Performance', icon: 'activity', route: '/admin/page-performance', group: 'tenant', requiredPermission: 'developer:technical' },
    { id: 'settings-pwa-policies', slug: 'pwa-policies', label: 'PWA Settings', labelPlural: 'PWA Install Policies', icon: 'smartphone', route: '/admin/pwa-policies', group: 'tenant' },
    { id: 'settings-surface-access', slug: 'surface-access', label: 'Surface Access', labelPlural: 'Surface Access', icon: 'globe', route: '/settings/surface-access', group: 'tenant' },
    { id: 'settings-running-numbers', slug: 'running-numbers', label: 'Running Numbers', labelPlural: 'Running Numbers', icon: 'hash', route: '/settings/running-numbers', group: 'tenant' },
    { id: 'settings-recycle-bin', slug: 'recycle-bin', label: 'Recycle Bin', labelPlural: 'Recycle Bin', icon: 'archive', route: '/settings/recycle-bin', group: 'audit' },
    { id: 'settings-activity-log', slug: 'activity-log', label: 'Activity Log', labelPlural: 'Activity Log', icon: 'activity', route: '/settings/activity-log', group: 'audit' },
    { id: 'settings-event-log', slug: 'event-log', label: 'Event Log', labelPlural: 'Event Log', icon: 'scroll-text', route: '/settings/event-log', group: 'audit' },
    { id: 'settings-general', slug: 'general', label: 'General', labelPlural: 'General Settings', icon: 'settings', route: '/settings/general', group: 'personal' },
    { id: 'settings-profile', slug: 'profile', label: 'Profile', labelPlural: 'My Profile', icon: 'user', route: '/settings/profile', group: 'personal' },
    { id: 'settings-team', slug: 'team', label: 'Team', labelPlural: 'Team Members', icon: 'users', route: '/settings/team', group: 'tenant' },
    { id: 'settings-templates', slug: 'templates', label: 'Templates', labelPlural: 'Document Templates', icon: 'file-text', route: '/settings/templates', group: 'tenant' },
    { id: 'settings-knowledge-base', slug: 'knowledge-base', label: 'Knowledge Base', labelPlural: 'Knowledge Base', icon: 'brain', route: '/settings/knowledge-base', group: 'tenant' },
    { id: 'settings-billing', slug: 'billing', label: 'Billing', labelPlural: 'Billing', icon: 'credit-card', route: '/settings/billing', group: 'tenant' },
    { id: 'settings-builder', slug: 'builder', label: 'Builder', labelPlural: 'Builder Studio', icon: 'wand-2', route: '/admin/builder', group: 'tenant', requiredPermission: 'developer:technical' },
    { id: 'settings-business-packages', slug: 'business-packages', label: 'Business Packages', labelPlural: 'Business Packages', icon: 'package', route: '/admin/packs', group: 'tenant', requiredPermission: 'developer:technical' },
    { id: 'settings-sdk-data', slug: 'sdk-data', label: 'SDK Data', labelPlural: 'SDK Data', icon: 'database', route: '/admin/sdk-data', group: 'tenant' },
  ],

  platform: [
    { id: 'platform-dynamic', slug: 'platform-dynamic', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/admin/platform-dynamic', group: 'platform' },
    { id: 'platform-onboarding-control', slug: 'onboarding-control', label: 'Onboarding Control', labelPlural: 'Onboarding Control', icon: 'route', route: '/admin/onboarding-control', group: 'platform' },
    { id: 'platform-onboarding-records', slug: 'onboarding-records', label: 'Onboarding Records', labelPlural: 'Onboarding Records', icon: 'clipboard-list', route: '/admin/onboarding-records', group: 'platform' },
    { id: 'platform-onboarding-assignment-policies', slug: 'onboarding-assignment-policies', label: 'Onboarding Assignment Policies', labelPlural: 'Onboarding Assignment Policies', icon: 'settings-2', route: '/admin/onboarding-assignment-policies', group: 'platform' },
    { id: 'platform-menu-config', slug: 'menu-config', label: 'Menu Config', labelPlural: 'Menu Config', icon: 'list-checks', route: '/admin/menu-config', group: 'platform' },
    { id: 'platform-tenants', slug: 'tenants', label: 'Tenants', labelPlural: 'Tenants', icon: 'building-2', route: '/admin/tenants', group: 'platform' },
    { id: 'platform-users', slug: 'users', label: 'Global Users', labelPlural: 'Global Users', icon: 'users', route: '/admin/users', group: 'platform' },
    { id: 'platform-user-roles', slug: 'user-roles', label: 'User Roles', labelPlural: 'User Roles', icon: 'shield', route: '/admin/user-roles', group: 'platform' },
    { id: 'platform-deploy', slug: 'deploy-control', label: 'Deploy Control', labelPlural: 'Deploy Control', icon: 'rocket', route: '/admin/deploy-control', group: 'devops' },
  ],

  // ── CC Admin Console Categories (platform_admin only) ─────────────────────

  'cc-command': [
    { id: 'cc-command-dashboard', slug: 'cc-command-dashboard', label: 'Boox Command', labelPlural: 'Boox Command', labelKey: 'nav.booxCommand', labelPluralKey: 'nav.booxCommand', icon: 'layout-dashboard', route: '/admin/platform-dynamic', group: 'overview' },
    { id: 'cc-command-chat', slug: 'cc-command-chat', label: 'AI Chat', labelPlural: 'AI Chat', labelKey: 'nav.aiChat', labelPluralKey: 'nav.aiChat', icon: 'bot', route: '/chat', group: 'tools' },
    { id: 'cc-command-design-copilot', slug: 'cc-command-design-copilot', label: 'Design Copilot', labelPlural: 'Design Copilot', icon: 'wand-2', route: '/admin/design-copilot', group: 'tools' },
    { id: 'cc-command-brain-monitor', slug: 'cc-command-brain-monitor', label: 'Brain Monitor', labelPlural: 'Brain Monitor', labelKey: 'nav.brainMonitor', labelPluralKey: 'nav.brainMonitor', icon: 'activity', route: '/admin/brain-monitor', group: 'runtime' },
    { id: 'cc-command-runtime-debug', slug: 'cc-command-runtime-debug', label: 'Runtime Debug', labelPlural: 'Runtime Debug', labelKey: 'nav.runtimeDebug', labelPluralKey: 'nav.runtimeDebug', icon: 'activity', route: '/admin/runtime-debug', group: 'runtime' },
    { id: 'cc-command-alerts', slug: 'cc-command-alerts', label: 'Alert Center', labelPlural: 'Alert Center', labelKey: 'nav.alertCenter', labelPluralKey: 'nav.alertCenter', icon: 'bell', route: '/admin/alerts', group: 'runtime' },
    { id: 'cc-command-rollout', slug: 'cc-command-rollout', label: 'Rollout Dashboard', labelPlural: 'Rollout Dashboard', labelKey: 'nav.rolloutDashboard', labelPluralKey: 'nav.rolloutDashboard', icon: 'rocket', route: '/admin/rollout-dashboard', group: 'runtime' },
  ],

  'cc-revenue': [
    { id: 'cc-revenue-inbox', slug: 'cc-revenue-inbox', label: 'Revenue Inbox', labelPlural: 'Revenue Inbox', labelKey: 'nav.revenueInbox', labelPluralKey: 'nav.revenueInbox', icon: 'inbox', route: '/ai/inbox?copilot=intake&pipeline=inbox', group: 'intake' },
    { id: 'cc-revenue-messaging', slug: 'cc-revenue-messaging', label: 'Messaging Intake', labelPlural: 'Messaging Intake', labelKey: 'nav.messagingIntake', labelPluralKey: 'nav.messagingIntake', icon: 'mail', route: '/inbox/messaging', group: 'intake' },
    { id: 'cc-revenue-surface-access', slug: 'cc-revenue-surface-access', label: 'Surface Access', labelPlural: 'Surface Access', labelKey: 'nav.surfaceAccess', labelPluralKey: 'nav.surfaceAccess', icon: 'globe', route: '/admin/surface-access', group: 'intake' },
    { id: 'cc-revenue-partner-applications', slug: 'cc-revenue-partner-applications', label: 'Partner Applications', labelPlural: 'Partner Applications', labelKey: 'nav.partnerApplications', labelPluralKey: 'nav.partnerApplications', icon: 'handshake', route: '/admin/partner-applications', group: 'qualification' },
    { id: 'cc-revenue-onboarding-control', slug: 'cc-revenue-onboarding-control', label: 'Onboarding Control', labelPlural: 'Onboarding Control', labelKey: 'nav.onboardingControl', labelPluralKey: 'nav.onboardingControl', icon: 'route', route: '/admin/onboarding-control', group: 'qualification' },
    { id: 'cc-revenue-onboarding-records', slug: 'cc-revenue-onboarding-records', label: 'Onboarding Records', labelPlural: 'Onboarding Records', labelKey: 'nav.onboardingRecords', labelPluralKey: 'nav.onboardingRecords', icon: 'clipboard-list', route: '/admin/onboarding-records', group: 'qualification' },
    { id: 'cc-revenue-assignment-policies', slug: 'cc-revenue-assignment-policies', label: 'Assignment Policies', labelPlural: 'Assignment Policies', labelKey: 'nav.assignmentPolicies', labelPluralKey: 'nav.assignmentPolicies', icon: 'settings-2', route: '/admin/onboarding-assignment-policies', group: 'governance' },
  ],

  'cc-delivery': [
    { id: 'cc-delivery-teamspace', slug: 'cc-delivery-teamspace', label: 'Teamspace', labelPlural: 'Teamspace', labelKey: 'nav.teamspace', labelPluralKey: 'nav.teamspace', icon: 'workflow', route: '/admin/teamspace', group: 'delivery' },
    { id: 'cc-delivery-capability-matrix', slug: 'cc-delivery-capability-matrix', label: 'Capability Matrix', labelPlural: 'Capability Matrix', labelKey: 'nav.capabilityMatrix', labelPluralKey: 'nav.capabilityMatrix', icon: 'grid-3x3', route: '/admin/capability-matrix', group: 'delivery' },
    { id: 'cc-delivery-rollout', slug: 'cc-delivery-rollout', label: 'Rollout Dashboard', labelPlural: 'Rollout Dashboard', labelKey: 'nav.rolloutDashboard', labelPluralKey: 'nav.rolloutDashboard', icon: 'rocket', route: '/admin/rollout-dashboard', group: 'readiness' },
    { id: 'cc-delivery-state-console', slug: 'cc-delivery-state-console', label: 'State Console', labelPlural: 'State Console', labelKey: 'nav.stateConsole', labelPluralKey: 'nav.stateConsole', icon: 'database', route: '/admin/state-console', group: 'readiness' },
    { id: 'cc-delivery-einvoicing', slug: 'cc-delivery-einvoicing', label: 'E-Invoicing Ops', labelPlural: 'E-Invoicing Ops', labelKey: 'nav.eInvoicingOps', labelPluralKey: 'nav.eInvoicingOps', icon: 'file-text', route: '/admin/e-invoicing-ops', group: 'runtime' },
  ],

  'cc-ceo': [
    { id: 'cc-ceo-desk', slug: 'cc-ceo-desk', label: 'CEO Desk', labelPlural: 'CEO Desk', labelKey: 'nav.ceoDesk', labelPluralKey: 'nav.ceoDesk', icon: 'briefcase', route: '/admin/ceo-desk', group: 'executive' },
    { id: 'cc-ceo-strategy', slug: 'cc-ceo-strategy', label: 'Strategy', labelPlural: 'Strategy', labelKey: 'nav.strategy', labelPluralKey: 'nav.strategy', icon: 'target', route: '/admin/ceo-desk?panel=strategy', group: 'executive' },
    { id: 'cc-ceo-fundraising', slug: 'cc-ceo-fundraising', label: 'Fundraising', labelPlural: 'Fundraising', labelKey: 'nav.fundraising', labelPluralKey: 'nav.fundraising', icon: 'landmark', route: '/admin/ceo-desk?panel=fundraising', group: 'executive' },
    { id: 'cc-ceo-dataroom', slug: 'cc-ceo-dataroom', label: 'Data Room', labelPlural: 'Data Room', labelKey: 'nav.dataRoom', labelPluralKey: 'nav.dataRoom', icon: 'folder-kanban', route: '/admin/ceo-desk?panel=dataroom', group: 'executive' },
    { id: 'cc-ceo-coco', slug: 'cc-ceo-coco', label: 'AI Co-CEO', labelPlural: 'AI Co-CEO', labelKey: 'nav.aiCoCeo', labelPluralKey: 'nav.aiCoCeo', icon: 'bot', route: '/admin/ceo-desk?panel=co-ceo', group: 'executive' },
  ],

  'cc-tenant-admin': [
    { id: 'cc-tenant-admin-tenants', slug: 'cc-tenant-admin-tenants', label: 'Tenants', labelPlural: 'Tenants', labelKey: 'nav.tenants', labelPluralKey: 'nav.tenants', icon: 'building-2', route: '/admin/tenants', group: 'governance' },
    { id: 'cc-tenant-admin-users', slug: 'cc-tenant-admin-users', label: 'Global Users', labelPlural: 'Global Users', labelKey: 'nav.globalUsers', labelPluralKey: 'nav.globalUsers', icon: 'users', route: '/admin/users', group: 'governance' },
    { id: 'cc-tenant-admin-roles', slug: 'cc-tenant-admin-roles', label: 'User Roles', labelPlural: 'User Roles', labelKey: 'nav.userRoles', labelPluralKey: 'nav.userRoles', icon: 'shield', route: '/admin/user-roles', group: 'governance' },
    { id: 'cc-tenant-admin-api-center', slug: 'cc-tenant-admin-api-center', label: 'API Center', labelPlural: 'API Center', labelKey: 'nav.apiCenter', labelPluralKey: 'nav.apiCenter', icon: 'key', route: '/admin/api-center', group: 'integration' },
    { id: 'cc-tenant-admin-rest-api', slug: 'cc-tenant-admin-rest-api', label: 'REST API', labelPlural: 'REST API', icon: 'globe', route: '/admin/api-center?tab=rest', group: 'integration' },
    { id: 'cc-tenant-admin-mcp-management', slug: 'cc-tenant-admin-mcp-management', label: 'MCP Management', labelPlural: 'MCP Management', icon: 'plug', route: '/admin/api-center?tab=mcp', group: 'integration' },
    { id: 'cc-tenant-admin-llm-intake-test', slug: 'cc-tenant-admin-llm-intake-test', label: 'LLM Intake Test', labelPlural: 'LLM Intake Test', icon: 'wand-2', route: '/admin/api-center?tab=llm-intake', group: 'integration' },
    { id: 'cc-tenant-admin-capabilities', slug: 'cc-tenant-admin-capabilities', label: 'Capabilities', labelPlural: 'Capabilities', labelKey: 'nav.capabilities', labelPluralKey: 'nav.capabilities', icon: 'toggle-right', route: '/admin/capabilities', group: 'configuration' },
    { id: 'cc-tenant-admin-policies', slug: 'cc-tenant-admin-policies', label: 'Policies', labelPlural: 'Policies', labelKey: 'nav.policies', labelPluralKey: 'nav.policies', icon: 'shield-check', route: '/admin/policies', group: 'configuration' },
    { id: 'cc-tenant-admin-exception-policies', slug: 'cc-tenant-admin-exception-policies', label: 'Exception Policies', labelPlural: 'Exception Policies', labelKey: 'nav.exceptionPolicies', labelPluralKey: 'nav.exceptionPolicies', icon: 'alert-triangle', route: '/admin/exception-policies', group: 'configuration' },
    { id: 'cc-tenant-admin-workflow-policies', slug: 'cc-tenant-admin-workflow-policies', label: 'Workflow Policies', labelPlural: 'Workflow Policies', labelKey: 'nav.workflowPolicies', labelPluralKey: 'nav.workflowPolicies', icon: 'git-branch', route: '/admin/workflow-policies', group: 'configuration' },
    { id: 'cc-tenant-admin-crm-intelligence-rules', slug: 'cc-tenant-admin-crm-intelligence-rules', label: 'RM Intelligence Rules', labelPlural: 'RM Intelligence Rules', icon: 'brain-circuit', route: '/admin/crm-intelligence-rules', group: 'configuration' },
    { id: 'cc-tenant-admin-whatsapp', slug: 'cc-tenant-admin-whatsapp', label: 'WhatsApp', labelPlural: 'WhatsApp', labelKey: 'nav.whatsAppAdmin', labelPluralKey: 'nav.whatsAppAdmin', icon: 'message-square', route: '/admin/whatsapp', group: 'configuration' },
  ],

  'cc-systems': [
    { id: 'cc-systems-home', slug: 'cc-systems-home', label: 'Platform Systems', labelPlural: 'Platform Systems', labelKey: 'nav.platformSystems', labelPluralKey: 'nav.platformSystems', icon: 'settings', route: '/admin/platform-systems', group: 'overview' },
    { id: 'cc-systems-sdk-data', slug: 'cc-systems-sdk-data', label: 'SDK Data', labelPlural: 'SDK Data', icon: 'database', route: '/admin/sdk-data', group: 'overview' },
    { id: 'cc-systems-builder', slug: 'cc-systems-builder', label: 'Builder', labelPlural: 'Builder', labelKey: 'nav.builder', labelPluralKey: 'nav.builder', icon: 'wand-2', route: '/admin/builder', group: 'build' },
    { id: 'cc-systems-business-packages', slug: 'cc-systems-business-packages', label: 'Business Packages', labelPlural: 'Business Packages', labelKey: 'nav.businessPackages', labelPluralKey: 'nav.businessPackages', icon: 'package', route: '/admin/packs', group: 'build' },
    { id: 'cc-systems-schema', slug: 'cc-systems-schema', label: 'Schema Editor', labelPlural: 'Schema Editor', labelKey: 'nav.schemaEditor', labelPluralKey: 'nav.schemaEditor', icon: 'database', route: '/admin/schema', group: 'build' },
    { id: 'cc-systems-dsl-designer', slug: 'cc-systems-dsl-designer', label: 'DSL Designer', labelPlural: 'DSL Designer', labelKey: 'nav.dslDesigner', labelPluralKey: 'nav.dslDesigner', icon: 'layout', route: '/admin/dsl-designer', group: 'build' },
    { id: 'cc-systems-view-templates', slug: 'cc-systems-view-templates', label: 'View Templates', labelPlural: 'View Templates', labelKey: 'nav.viewTemplates', labelPluralKey: 'nav.viewTemplates', icon: 'eye', route: '/admin/view-templates', group: 'build' },
    { id: 'cc-systems-doc-templates', slug: 'cc-systems-doc-templates', label: 'Document Templates', labelPlural: 'Document Templates', labelKey: 'nav.documentTemplates', labelPluralKey: 'nav.documentTemplates', icon: 'file-text', route: '/admin/templates', group: 'build' },
    { id: 'cc-systems-workflows', slug: 'cc-systems-workflows', label: 'Workflows', labelPlural: 'Workflows', labelKey: 'nav.workflows', labelPluralKey: 'nav.workflows', icon: 'git-branch', route: '/admin/workflows', group: 'build' },
    { id: 'cc-systems-workflow-templates', slug: 'cc-systems-workflow-templates', label: 'Workflow Templates', labelPlural: 'Workflow Templates', labelKey: 'nav.workflowTemplates', labelPluralKey: 'nav.workflowTemplates', icon: 'copy', route: '/admin/workflow-templates', group: 'build' },
    { id: 'cc-systems-scripts', slug: 'cc-systems-scripts', label: 'Scripts', labelPlural: 'Scripts', labelKey: 'nav.scripts', labelPluralKey: 'nav.scripts', icon: 'terminal', route: '/admin/scripts', group: 'build' },
    { id: 'cc-systems-config-compiler', slug: 'cc-systems-config-compiler', label: 'Config Compiler', labelPlural: 'Config Compiler', labelKey: 'nav.configCompiler', labelPluralKey: 'nav.configCompiler', icon: 'cpu', route: '/admin/config-compiler', group: 'configuration' },
    { id: 'cc-systems-config-versions', slug: 'cc-systems-config-versions', label: 'Config Versions', labelPlural: 'Config Versions', labelKey: 'nav.configVersions', labelPluralKey: 'nav.configVersions', icon: 'history', route: '/admin/config-versions', group: 'configuration' },
    { id: 'cc-systems-domain-configs', slug: 'cc-systems-domain-configs', label: 'Domain Configs', labelPlural: 'Domain Configs', labelKey: 'nav.domainConfigs', labelPluralKey: 'nav.domainConfigs', icon: 'folder-cog', route: '/admin/domain-configs', group: 'configuration' },
    { id: 'cc-systems-menu-config', slug: 'cc-systems-menu-config', label: 'Menu Config', labelPlural: 'Menu Config', labelKey: 'nav.menuConfig', labelPluralKey: 'nav.menuConfig', icon: 'list-checks', route: '/admin/menu-config', group: 'configuration' },
    { id: 'cc-systems-ai-command', slug: 'cc-systems-ai-command', label: 'AI Command Center', labelPlural: 'AI Command Center', labelKey: 'nav.aiCommandCenter', labelPluralKey: 'nav.aiCommandCenter', icon: 'radio', route: '/admin/command-center', group: 'ai-runtime' },
    { id: 'cc-systems-ai-team', slug: 'cc-systems-ai-team', label: 'AI Team', labelPlural: 'AI Team', labelKey: 'nav.aiTeam', labelPluralKey: 'nav.aiTeam', icon: 'users', route: '/admin/team', group: 'ai-runtime' },
    { id: 'cc-systems-agent-lab', slug: 'cc-systems-agent-lab', label: 'Agent Lab', labelPlural: 'Agent Lab', labelKey: 'nav.agentLab', labelPluralKey: 'nav.agentLab', icon: 'flask-conical', route: '/admin/agentlab', group: 'ai-runtime' },
    { id: 'cc-systems-skills', slug: 'cc-systems-skills', label: 'Skills', labelPlural: 'Skills', labelKey: 'nav.skills', labelPluralKey: 'nav.skills', icon: 'puzzle', route: '/admin/skills', group: 'ai-runtime' },
    { id: 'cc-systems-skill-config', slug: 'cc-systems-skill-config', label: 'Skill Config', labelPlural: 'Skill Config', labelKey: 'nav.skillConfig', labelPluralKey: 'nav.skillConfig', icon: 'sliders-horizontal', route: '/admin/skill-config', group: 'ai-runtime' },
    { id: 'cc-systems-prompts', slug: 'cc-systems-prompts', label: 'Prompts', labelPlural: 'Prompts', labelKey: 'nav.prompts', labelPluralKey: 'nav.prompts', icon: 'message-circle', route: '/admin/prompts', group: 'ai-runtime' },
    { id: 'cc-systems-training', slug: 'cc-systems-training', label: 'Training Data', labelPlural: 'Training Data', labelKey: 'nav.trainingData', labelPluralKey: 'nav.trainingData', icon: 'graduation-cap', route: '/admin/training', group: 'ai-runtime' },
    { id: 'cc-systems-memory', slug: 'cc-systems-memory', label: 'Memory', labelPlural: 'Memory', labelKey: 'nav.memory', labelPluralKey: 'nav.memory', icon: 'brain', route: '/admin/memory', group: 'ai-runtime' },
    { id: 'cc-systems-tools', slug: 'cc-systems-tools', label: 'Tools', labelPlural: 'Tools', labelKey: 'nav.tools', labelPluralKey: 'nav.tools', icon: 'hammer', route: '/admin/tools', group: 'ai-runtime' },
    { id: 'cc-systems-ai-control', slug: 'cc-systems-ai-control', label: 'AI Control Plane', labelPlural: 'AI Control Plane', labelKey: 'nav.aiControlPlane', labelPluralKey: 'nav.aiControlPlane', icon: 'gauge', route: '/admin/ai-control-plane', group: 'ai-runtime' },
    { id: 'cc-systems-design-copilot', slug: 'cc-systems-design-copilot', label: 'Design Copilot', labelPlural: 'Design Copilot', icon: 'wand-2', route: '/admin/design-copilot', group: 'ai-runtime' },
    { id: 'cc-systems-runtime-debug', slug: 'cc-systems-runtime-debug', label: 'Runtime Debug', labelPlural: 'Runtime Debug', labelKey: 'nav.runtimeDebug', labelPluralKey: 'nav.runtimeDebug', icon: 'activity', route: '/admin/runtime-debug', group: 'ai-runtime' },
    { id: 'cc-systems-brain-logs', slug: 'cc-systems-brain-logs', label: 'Brain Logs', labelPlural: 'Brain Logs', labelKey: 'nav.brainLogs', labelPluralKey: 'nav.brainLogs', icon: 'scroll-text', route: '/admin/brain-logs', group: 'ai-runtime' },
    { id: 'cc-systems-brain-soul', slug: 'cc-systems-brain-soul', label: 'Brain Soul', labelPlural: 'Brain Soul', labelKey: 'nav.brainSoul', labelPluralKey: 'nav.brainSoul', icon: 'sparkles', route: '/admin/brain-soul', group: 'ai-runtime' },
    { id: 'cc-systems-brain-schedule', slug: 'cc-systems-brain-schedule', label: 'Brain Schedule', labelPlural: 'Brain Schedule', labelKey: 'nav.brainSchedule', labelPluralKey: 'nav.brainSchedule', icon: 'calendar-clock', route: '/admin/brain-schedule', group: 'ai-runtime' },
    { id: 'cc-systems-recurring-tasks', slug: 'cc-systems-recurring-tasks', label: 'Recurring Tasks', labelPlural: 'Recurring Tasks', labelKey: 'nav.recurringTasks', labelPluralKey: 'nav.recurringTasks', icon: 'repeat', route: '/admin/recurring-tasks', group: 'ai-runtime' },
    { id: 'cc-systems-task-center', slug: 'cc-systems-task-center', label: 'Task Center', labelPlural: 'Task Center', labelKey: 'nav.taskCenter', labelPluralKey: 'nav.taskCenter', icon: 'list-checks', route: '/admin/tasks', group: 'ai-runtime' },
    { id: 'cc-systems-executive-nudges', slug: 'cc-systems-executive-nudges', label: 'Executive Nudges', labelPlural: 'Executive Nudges', labelKey: 'nav.executiveNudges', labelPluralKey: 'nav.executiveNudges', icon: 'bell-ring', route: '/admin/executive-nudges', group: 'ai-runtime' },
    { id: 'cc-systems-bridges', slug: 'cc-systems-bridges', label: 'Bridges', labelPlural: 'Bridges', labelKey: 'nav.bridges', labelPluralKey: 'nav.bridges', icon: 'plug', route: '/admin/bridges', group: 'systems' },
    { id: 'cc-systems-legacy-connectors', slug: 'cc-systems-legacy-connectors', label: 'Legacy Connectors', labelPlural: 'Legacy Connectors', icon: 'database', route: '/admin/legacy-connectors', group: 'systems' },
    { id: 'cc-systems-data-transfer', slug: 'cc-systems-data-transfer', label: 'Data Transfer', labelPlural: 'Data Transfer', labelKey: 'nav.dataTransfer', labelPluralKey: 'nav.dataTransfer', icon: 'arrow-left-right', route: '/admin/data-transfer', group: 'systems' },
    { id: 'cc-systems-deploy-control', slug: 'cc-systems-deploy-control', label: 'Deploy Control', labelPlural: 'Deploy Control', labelKey: 'nav.deployControl', labelPluralKey: 'nav.deployControl', icon: 'rocket', route: '/admin/deploy-control', group: 'systems' },
    { id: 'cc-systems-print-center', slug: 'cc-systems-print-center', label: 'Print Center', labelPlural: 'Print Center', labelKey: 'nav.printCenter', labelPluralKey: 'nav.printCenter', icon: 'printer', route: '/admin/print-center', group: 'systems' },
    { id: 'cc-systems-analytics', slug: 'cc-systems-analytics', label: 'Platform Analytics', labelPlural: 'Platform Analytics', labelKey: 'nav.platformAnalytics', labelPluralKey: 'nav.platformAnalytics', icon: 'bar-chart-3', route: '/admin/analytics', group: 'monitoring' },
    { id: 'cc-systems-page-performance', slug: 'cc-systems-page-performance', label: 'Page Performance', labelPlural: 'Page Performance', icon: 'activity', route: '/admin/page-performance', group: 'monitoring' },
    { id: 'cc-systems-logs', slug: 'cc-systems-logs', label: 'Log Explorer', labelPlural: 'Log Explorer', labelKey: 'nav.logExplorer', labelPluralKey: 'nav.logExplorer', icon: 'search', route: '/admin/logs', group: 'monitoring' },
    { id: 'cc-systems-access-log', slug: 'cc-systems-access-log', label: 'Access Log', labelPlural: 'Access Log', labelKey: 'nav.accessLog', labelPluralKey: 'nav.accessLog', icon: 'activity', route: '/admin/access-log', group: 'monitoring' },
    { id: 'cc-systems-user-performance', slug: 'cc-systems-user-performance', label: 'User Performance', labelPlural: 'User Performance', labelKey: 'nav.userPerformance', labelPluralKey: 'nav.userPerformance', icon: 'user-check', route: '/admin/user-performance', group: 'monitoring' },
    { id: 'cc-systems-job-timeline', slug: 'cc-systems-job-timeline', label: 'Job Timeline', labelPlural: 'Job Timeline', labelKey: 'nav.jobTimeline', labelPluralKey: 'nav.jobTimeline', icon: 'gantt-chart', route: '/admin/job-timeline', group: 'monitoring' },
  ],

  'supply-chain': [
    // ── Dashboard ──
    { id: 'scm-dashboard', slug: 'scm', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/scm', group: 'dashboard' },
    // ── Sales ──
    { id: 'scm-sales-orders', slug: 'sales-orders', label: 'Sales Order', labelPlural: 'Sales Orders', icon: 'shopping-cart', route: '/scm/sales-orders', group: 'sales' },
    { id: 'scm-quotations', slug: 'scm-quotations', label: 'Quotation', labelPlural: 'Quotations', icon: 'file-text', route: '/scm/quotations', group: 'sales' },
    { id: 'scm-salespersons', slug: 'scm-salespersons', label: 'Salesperson', labelPlural: 'Salespersons', icon: 'user-check', route: '/scm/salespersons', group: 'sales' },
    // ── Procurement ──
    { id: 'scm-purchase-orders', slug: 'purchase-orders', label: 'Purchase Order', labelPlural: 'Purchase Orders', icon: 'clipboard-list', route: '/scm/purchase-orders', group: 'procurement' },
    { id: 'scm-suppliers', slug: 'suppliers', label: 'Supplier', labelPlural: 'Suppliers', icon: 'building-2', route: '/scm/suppliers', group: 'procurement' },
    { id: 'scm-rfq', slug: 'scm-rfq', label: 'RFQ', labelPlural: 'RFQ', icon: 'file-search', route: '/scm/rfq', group: 'procurement' },
    // ── Inventory ──
    { id: 'scm-products', slug: 'products', label: 'Product', labelPlural: 'Products', icon: 'box', route: '/scm/products', group: 'inventory' },
    { id: 'scm-inventory', slug: 'scm-inventory', label: 'Inventory', labelPlural: 'Inventory', icon: 'database', route: '/scm/inventory', group: 'inventory' },
    { id: 'scm-stock-movements', slug: 'scm-stock-movements', label: 'Stock Movement', labelPlural: 'Stock Movements', icon: 'arrow-left-right', route: '/scm/stock-movements', group: 'inventory' },
    // ── Fulfillment ──
    { id: 'scm-fulfillment', slug: 'fulfillment', label: 'Fulfillment', labelPlural: 'Fulfillment', icon: 'package-check', route: '/scm/fulfillment', group: 'fulfillment' },
    { id: 'scm-shipment-tracking', slug: 'shipment-tracking', label: 'Shipment Tracking', labelPlural: 'Shipment Tracking', icon: 'map-pin', route: '/scm/shipment-tracking', group: 'fulfillment' },
    // ── Costing & Reports ──
    { id: 'scm-costing', slug: 'scm-costing', label: 'Costing', labelPlural: 'Costing', icon: 'calculator', route: '/scm/costing', group: 'reports' },
    { id: 'scm-reports', slug: 'scm-reports', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/scm/reports', group: 'reports' },
  ],
  service: [
    // ── Dashboard ──
    { id: 'svc-dashboard', slug: 'svc', label: 'Dashboard', labelPlural: 'Dashboard', icon: 'layout-dashboard', route: '/svc', group: 'dashboard' },
    // ── Orders ──
    { id: 'svc-orders', slug: 'svc-orders', label: 'Service Order', labelPlural: 'Service Orders', icon: 'clipboard-list', route: '/svc/orders', group: 'orders' },
    { id: 'svc-dispatch', slug: 'svc-dispatch', label: 'Dispatch', labelPlural: 'Dispatch', icon: 'calendar', route: '/svc/dispatch', group: 'orders' },
    // ── Resources ──
    { id: 'svc-equipment', slug: 'svc-equipment', label: 'Equipment', labelPlural: 'Equipment', icon: 'cog', route: '/svc/equipment', group: 'resources' },
    { id: 'svc-crew', slug: 'svc-crew', label: 'Crew', labelPlural: 'Crews', icon: 'hard-hat', route: '/svc/crew', group: 'resources' },
    { id: 'svc-sites', slug: 'svc-sites', label: 'Sites', labelPlural: 'Sites', icon: 'map-pin', route: '/svc/sites', group: 'resources' },
    // ── Finance ──
    { id: 'svc-finance', slug: 'svc-finance', label: 'Finance', labelPlural: 'Finance', icon: 'dollar-sign', route: '/svc/finance', group: 'finance' },
    // ── Reports ──
    { id: 'svc-reports', slug: 'svc-reports', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/svc/reports', group: 'reports' },
  ],
  warehouse: [],
  finance: [
    // ── Transactions (daily bookkeeping — QB: Bookkeeping, Xero: Business) ──
    { id: 'fin-ar',          slug: 'ar',           label: 'Accounts Receivable', labelPlural: 'Accounts Receivable', icon: 'arrow-down-circle',  route: '/finance/ar',          group: 'transactions' },
    { id: 'fin-ap',          slug: 'ap',           label: 'Accounts Payable',    labelPlural: 'Accounts Payable',    icon: 'arrow-up-circle',    route: '/finance/ap',          group: 'transactions' },
    { id: 'fin-receipts',    slug: 'receipts',     label: 'Receipts',            labelPlural: 'Receipts',            icon: 'wallet',             route: '/finance/receipts',    group: 'transactions' },
    { id: 'fin-payments',    slug: 'payments',     label: 'Payments',            labelPlural: 'Payments',            icon: 'send',               route: '/finance/payments',    group: 'transactions' },
    { id: 'fin-journal',     slug: 'journal',      label: 'Journal Entry',       labelPlural: 'Journal Entries',     icon: 'book-open',          route: '/finance/journal',     group: 'transactions' },
    // ── Banking (bank feeds & reconciliation — Xero: Bank accounts) ──
    { id: 'fin-bank-statements',  slug: 'bank-statements',  label: 'Bank Statements', labelPlural: 'Bank Statements',  icon: 'file-text',     route: '/finance/bank-statements',  group: 'banking' },
    { id: 'fin-reconciliation',   slug: 'reconciliation',   label: 'Reconciliation',  labelPlural: 'Reconciliation',   icon: 'git-compare',   route: '/finance/reconciliation',   group: 'banking' },
    // ── Collections & Payments (automated AR/AP processes) ────
    { id: 'fin-ar-collection',    slug: 'ar-collection',    label: 'AR Collection',   labelPlural: 'AR Collection',    icon: 'phone-call',    route: '/finance/ar-collection',    group: 'collections' },
    { id: 'fin-payment-run',      slug: 'payment-run',      label: 'Payment Run',     labelPlural: 'Payment Runs',     icon: 'play-circle',   route: '/finance/payment-run',      group: 'collections' },
    { id: 'fin-netting',          slug: 'netting',          label: 'AR/AP Netting',   labelPlural: 'AR/AP Netting',    icon: 'git-merge',     route: '/finance/netting',          group: 'collections' },
    // ── E-Invoicing (compliance — InvoiceNow / MyInvois) ─────
    { id: 'fin-einvoicing', slug: 'e-invoicing-lab', label: 'E-Invoicing', labelPlural: 'E-Invoicing', icon: 'zap', route: '/finance/e-invoicing-lab', group: 'einvoicing' },
    // ── Audit (controls & compliance) ─────────────────────────
    { id: 'audit-dashboard', slug: 'audit', label: 'Audit Overview', labelPlural: 'Audit Overview', icon: 'shield-check', route: '/audit', group: 'audit' },
    { id: 'audit-settlement', slug: 'settlement', label: 'Settlement', labelPlural: 'Settlement', icon: 'receipt', route: '/audit/settlement', group: 'audit' },
    { id: 'audit-disputes', slug: 'disputes', label: 'Disputes', labelPlural: 'Disputes', icon: 'scale', route: '/audit/disputes', group: 'audit' },
    { id: 'audit-bill-audit', slug: 'bill-audit', label: 'Bill Audit', labelPlural: 'Bill Audits', icon: 'clipboard-check', route: '/audit/bill-audit', group: 'audit' },
    { id: 'audit-exception-policies', slug: 'exception-policies', label: 'Exception Policies', labelPlural: 'Exception Policies', icon: 'alert-triangle', route: '/admin/exception-policies', group: 'audit' },
    { id: 'audit-workflow-policies', slug: 'workflow-policies', label: 'Workflow Policies', labelPlural: 'Workflow Policies', icon: 'git-branch', route: '/admin/workflow-policies', group: 'audit' },
    // ── Reports (unified hub — Operations / Financial / Management / Automation tabs) ──
    { id: 'fin-reports-hub', slug: 'reports-hub', label: 'Reports', labelPlural: 'Reports', icon: 'bar-chart-3', route: '/reports?tab=financial', group: 'reports' },
    // ── Setup (configuration — Xero: Advanced, QB: Chart of accounts) ──
    { id: 'fin-setup',              slug: 'setup',              label: 'Financial Setup',     labelPlural: 'Financial Setup',     icon: 'settings-2',       route: '/finance/setup',              group: 'setup' },
  ],

  workforce: [],
};

export function buildFallbackConfig(entities: EntityMeta[]): LayoutConfig {
  // Build navigation entirely from entity registry — no static tree.
  // ── Principle: sidebar is curated navigation, NOT an automatic projection
  // of the entity data catalog. Only categories explicitly listed in
  // DEFAULT_CATEGORY_ORDER are navigation concepts. Entity categories that
  // are purely data-layer (parties, transaction, event, config, agent) must
  // NOT leak into the sidebar. Those entities remain accessible via /e/:slug.
  const navSet = new Set(runtimeAugmentedCategoryOrder());

  const catMap = new Map<string, NavigationItem[]>();
  for (const e of entities) {
    if (HIDDEN_NAV_ITEM_IDS.has(e.slug)) continue;
    if (!navSet.has(e.category)) continue; // data-layer category, not a nav concept
    if (!catMap.has(e.category)) catMap.set(e.category, []);
    catMap.get(e.category)!.push({
      id: e.slug, slug: e.slug, label: e.label, labelPlural: e.labelPlural,
      icon: e.icon, route: `/e/${e.slug}`, group: e.group,
    });
  }

  // For categories with dedicated module pages, use MODULE_NAV_ITEMS instead of entity-derived routes.
  for (const [cat, moduleItems] of Object.entries(MODULE_NAV_ITEMS)) {
    if (!navSet.has(cat)) continue; // guard: MODULE_NAV_ITEMS keys must also be curated
    if (!catMap.has(cat)) catMap.set(cat, []);
    catMap.set(cat, moduleItems);
  }

  for (const binding of DYNAMIC_RUNTIME_BINDINGS) {
    if (!navSet.has(binding.categoryId)) continue;
    if (!catMap.has(binding.categoryId)) catMap.set(binding.categoryId, []);
  }

  const orderMap = new Map(DEFAULT_CATEGORY_ORDER.map((id, i) => [id, i]));
  const navigation: NavigationCategory[] = [...catMap.entries()].map(([cat, items]) => ({
    id: cat,
    label: categoryLabelFor(cat),
    labelKey: `category.${cat}`,
    icon: categoryIconFor(cat),
    order: categoryOrderFor(cat, orderMap),
    items,
    requiredPermission: categoryPermissionFor(cat),
    zone: categoryZoneFor(cat),
    requiredModule: categoryModuleFor(cat),
    dynamicRuntime: resolvedDynamicRuntimeBindingForCategory(cat),
  }));

  navigation.sort((a, b) => a.order - b.order);

  // Auto-detect KPI entities
  const kpiSlugs = ['jobs', 'ledger-entries', 'customers', 'shipments'];
  const kpiEntities = kpiSlugs
    .map((s) => entities.find((e) => e.slug === s))
    .filter(Boolean) as EntityMeta[];
  if (kpiEntities.length < 4) {
    const txn = entities.filter(
      (e) => e.category === 'transaction' && e.group !== 'devops' && !kpiSlugs.includes(e.slug),
    );
    for (const e of txn) {
      if (kpiEntities.length >= 4) break;
      kpiEntities.push(e);
    }
  }

  const kpiWidgets: KpiWidgetConfig[] = kpiEntities.map((e, idx) => ({
    slug: e.slug,
    label: e.labelPlural,
    color: DEFAULT_KPI_COLORS[idx % DEFAULT_KPI_COLORS.length]!,
    icon: e.icon,
    order: idx,
  }));

  // Auto-generate dashboard widgets
  const widgetEntities = entities.filter(
    (e) => e.statusValues.length > 0 && e.category === 'transaction' && e.group !== 'devops',
  );
  const autoWidgets: DashboardWidgetConfig[] = [];
  for (const e of widgetEntities.slice(0, 2)) {
    autoWidgets.push({ type: 'status-ring', slug: e.slug, title: `${e.labelPlural} by Status`, order: autoWidgets.length });
  }
  for (const e of kpiEntities.slice(0, 2)) {
    autoWidgets.push({ type: 'recent-list', slug: e.slug, title: `Recent ${e.labelPlural}`, limit: 5, order: autoWidgets.length });
  }

  return {
    locale: 'en',
    navigation,
    dashboard: {
      kpiWidgets,
      quickNavCategories: ['business', 'transport', 'freight'],
      widgets: autoWidgets,
    },
    branding: DEFAULT_BRANDING,
    features: DEFAULT_FEATURES,
    accentOptions: DEFAULT_ACCENT_OPTIONS,
    languages: DEFAULT_LANGUAGES,
    themes: DEFAULT_THEMES,
    categoryOrder: runtimeAugmentedCategoryOrder(),
    categoryIcons: runtimeAugmentedCategoryIcons(),
    notificationPollMs: 30_000,
    realtimeStatusAggregator: DEFAULT_REALTIME_STATUS_AGGREGATOR,
    isDefault: true,
  };
}

function buildServerOnlyEmptyConfig(): LayoutConfig {
  const baseline = buildFallbackConfig([]);
  return {
    ...baseline,
    navigation: [],
    dashboard: {
      ...baseline.dashboard,
      kpiWidgets: [],
      quickNavCategories: [],
      widgets: [],
    },
    pwaNavigation: undefined,
    extensionNavigation: undefined,
    strictNavigation: true,
    isDefault: true,
  };
}

// ─── Merge API config over server-only baseline ────

/**
 * Enrich API navigation categories with zone/module defaults.
 * API-provided items are the only menu source. Frontend module catalogs may
 * supply icon/zone metadata, but they must not create missing menu items.
 */
function navPolicyKeys(values?: string[]): Set<string> {
  return new Set((values ?? []).map((value) => value.trim().toLowerCase()).filter(Boolean));
}

function navItemKeys(item: NavigationItem): string[] {
  return [item.id, item.slug, item.route]
    .filter((key): key is string => Boolean(key))
    .map((key) => key.trim().toLowerCase());
}

function shouldHideNavCategory(categoryId: string, navigationPolicy?: NavigationPolicyConfig): boolean {
  return navPolicyKeys(navigationPolicy?.hiddenCategoryIds).has(categoryId.trim().toLowerCase());
}

function shouldHideNavItem(
  categoryId: string,
  item: NavigationItem,
  navigationPolicy?: NavigationPolicyConfig,
): boolean {
  const keys = navItemKeys(item);
  const foundationHiddenKeys = navPolicyKeys([...HIDDEN_NAV_ITEM_IDS]);
  if (keys.some((key) => foundationHiddenKeys.has(key))) return true;

  const policyHiddenKeys = new Set([
    ...navPolicyKeys(navigationPolicy?.hiddenItemIds),
    ...navPolicyKeys(navigationPolicy?.hiddenItemKeys),
    ...navPolicyKeys(navigationPolicy?.hiddenByCategory?.[categoryId]),
  ]);
  return keys.some((key) => policyHiddenKeys.has(key));
}

function normalizeNavigationItems(
  categoryId: string,
  items: NavigationItem[],
  navigationPolicy?: NavigationPolicyConfig,
): NavigationItem[] {
  const visibleItems = items
    .filter((item) => !shouldHideNavItem(categoryId, item, navigationPolicy))
    .map((item) => {
      if (item.id === 'wp-overview' || item.slug === 'overview') {
        return { ...item, route: '/wp' };
      }
      if (
        item.id === 'wp-issue-orders'
        || item.slug === 'issue-orders'
        || item.route === '/wp/issue-orders'
      ) {
        return { ...item, route: '/wp/outbound?outboundTab=outbound' };
      }
      if (
        item.id === 'wp-return-form'
        || item.slug === 'return-form'
        || item.route === '/wp/inbound/new?mode=RETURN'
      ) {
        return { ...item, label: 'Material Return', labelPlural: 'Material Return', route: '/wp/returns/form' };
      }
      if (
        item.id === 'wp-return-grn'
        || item.slug === 'return-grn'
      ) {
        return { ...item, label: 'Inventory Return', labelPlural: 'Inventory Return', route: '/wp/returns/grn' };
      }
      if (
        item.id === 'wp-return-project'
        || item.slug === 'return-from-project'
        || item.route === '/wp/returns/project'
      ) {
        return { ...item, label: 'Inventory Transfer', labelPlural: 'Inventory Transfer', route: '/wp/returns/project' };
      }
      if (
        item.id === 'wp-cycle-count'
        || item.slug === 'cycle-count'
        || item.route === '/wp/stock-take'
        || item.route === '/wp/cycle-counts'
      ) {
        return { ...item, label: 'Stock Count', labelPlural: 'Stock Count', route: '/wp/stock-take' };
      }
      return item;
    });
  if (categoryId !== 'warehouse') return visibleItems;
  return visibleItems.filter((item) => item.id !== 'wms-orders' && item.slug !== 'orders' && item.route !== '/wms/orders');
}

function enrichNavigation(nav: NavigationCategory[], navigationPolicy?: NavigationPolicyConfig): NavigationCategory[] {
  return nav
    .map((cat) => ({
      ...cat,
      label: cat.label ?? categoryLabelFor(cat.id),
      icon: cat.icon ?? categoryIconFor(cat.id),
      zone: cat.zone ?? categoryZoneFor(cat.id),
      requiredModule: cat.requiredModule ?? categoryModuleFor(cat.id),
      dynamicRuntime: cat.dynamicRuntime ?? resolvedDynamicRuntimeBindingForCategory(cat.id),
      requiredPermission: cat.id === 'workforce'
        ? '*'
        : (cat.requiredPermission ?? categoryPermissionFor(cat.id)),
      items: cat.items ?? [],
    }))
    .map((cat) => ({
      ...cat,
      items: normalizeNavigationItems(cat.id, cat.items, navigationPolicy),
    }))
    .filter((cat) => !shouldHideNavCategory(cat.id, navigationPolicy))
    .filter((cat) => cat.items.length > 0 || Boolean(cat.dynamicRuntime));
}

function buildEnabledModuleSet(enabledModules?: string[]): Set<string> {
  const moduleSet = new Set<string>(CORE_VISIBLE_MODULES);
  for (const mod of enabledModules ?? []) {
    if (typeof mod === 'string' && mod.trim()) moduleSet.add(mod.trim().toUpperCase());
  }
  if (moduleSet.has('AIC') || moduleSet.has('AIW')) moduleSet.add('AI');
  // Transition compatibility for modules that still rely on paired modern
  // categories outside the TM/WM cutover.
  if (moduleSet.has('TMS')) moduleSet.add('TM');
  if (moduleSet.has('HMS')) moduleSet.add('CT');
  if (moduleSet.has('FMS')) moduleSet.add('FM');
  if (moduleSet.has('FMS') || moduleSet.has('WMS') || moduleSet.has('KDCFS')) moduleSet.add('CF');
  if (moduleSet.has('WMS')) {
    moduleSet.add('WM');
    moduleSet.add('WP');
  }
  if (moduleSet.has('SCM')) moduleSet.add('SC');
  if (moduleSet.has('ACC')) moduleSet.add('AC');
  if (moduleSet.has('CRM')) moduleSet.add('RM');
  if (moduleSet.has('SAS') || moduleSet.has('DMS') || moduleSet.has('SOC')) moduleSet.add('MT');
  return moduleSet;
}

function canShowForModule(requiredModule: string | undefined, enabledModuleSet: Set<string>): boolean {
  if (!requiredModule) return true;
  return enabledModuleSet.has(requiredModule);
}

type CrmProfileKey = 'b2b_services' | 'product' | 'logistics' | 'freight';

const CRM_PROFILE_ALIASES: Record<string, CrmProfileKey> = {
  b2b: 'b2b_services',
  service: 'b2b_services',
  services: 'b2b_services',
  b2b_service: 'b2b_services',
  b2b_services: 'b2b_services',
  product: 'product',
  products: 'product',
  tyre: 'product',
  tire: 'product',
  logistics: 'logistics',
  logistic: 'logistics',
  transport: 'logistics',
  trucking: 'logistics',
  freight: 'freight',
  forwarding: 'freight',
  forwarder: 'freight',
  fms: 'freight',
};

const CRM_PROFILE_TERMS: Record<CrmProfileKey, LayoutConfig['crmTerminology']> = {
  b2b_services: {
    deal: 'Service Pipeline',
    quote: 'Service Quotes',
    followUp: 'Service Follow-ups',
    recovery: 'Service Recovery',
  },
  product: {
    account: 'Fleet / B2B Accounts',
    deal: 'Sales Pipeline',
    quote: 'Product / Service Quotes',
    followUp: 'Sales / Service Follow-ups',
    recovery: 'Warranty / Service Recovery',
    incentive: 'Commission',
  },
  logistics: {
    account: 'Logistics Accounts',
    deal: 'Lane / Service Pipeline',
    quote: 'Logistics Quotes',
    recovery: 'Delivery Recovery',
  },
  freight: {
    account: 'Shippers / Accounts',
    deal: 'Freight Pipeline',
    quote: 'Freight Quotes',
    signal: 'RFQ & Service Signals',
    recovery: 'Shipment Recovery',
  },
};

function normalizeCrmProfiles(values?: unknown[]): CrmProfileKey[] {
  const profiles: CrmProfileKey[] = [];
  for (const value of values ?? []) {
    if (typeof value !== 'string') continue;
    const key = CRM_PROFILE_ALIASES[value.trim().toLowerCase().replace(/[-\s]+/g, '_')];
    if (key && !profiles.includes(key)) profiles.push(key);
  }
  return profiles;
}

function inferCrmProfilesFromModules(enabledModules?: string[], tenantName?: string): CrmProfileKey[] {
  const name = (tenantName ?? '').toLowerCase();
  if (/(stamford|tyre|tire|automotive|fleet|product)/.test(name)) return ['product', 'b2b_services'];
  if (/(freight|forward|shipping|cargo|logistics)/.test(name)) return ['freight', 'logistics', 'b2b_services'];
  const modules = new Set((enabledModules ?? []).map((mod) => mod.toUpperCase()));
  if (modules.has('FMS')) return ['freight', 'logistics', 'b2b_services'];
  if (modules.has('TMS') || modules.has('HMS') || modules.has('FLM')) return ['logistics', 'b2b_services'];
  if (modules.has('SCM') || modules.has('SVC') || modules.has('WMS')) return ['product', 'b2b_services'];
  return ['b2b_services'];
}

function resolveCrmProfilesForConfig(configProfiles?: string[], enabledModules?: string[], tenantName?: string): CrmProfileKey[] {
  const configured = normalizeCrmProfiles(configProfiles);
  return configured.length > 0 ? configured : inferCrmProfilesFromModules(enabledModules, tenantName);
}

function resolveCrmTerms(
  profiles: CrmProfileKey[],
  overrides?: LayoutConfig['crmTerminology'],
): Required<NonNullable<LayoutConfig['crmTerminology']>> {
  const terms = {
    account: 'Accounts',
    contact: 'Contacts',
    deal: 'Pipeline',
    quote: 'Quotations',
    signal: 'Signals',
    followUp: 'Follow-ups',
    recovery: 'Recovery',
    incentive: 'Incentives',
  };
  for (const profile of profiles) Object.assign(terms, CRM_PROFILE_TERMS[profile]);
  return { ...terms, ...(overrides ?? {}) };
}

function applyCrmProfileToNavigation(
  navigation: NavigationCategory[],
  configProfiles?: string[],
  enabledModules?: string[],
  terminologyOverrides?: LayoutConfig['crmTerminology'],
  tenantName?: string,
): NavigationCategory[] {
  const profiles = resolveCrmProfilesForConfig(configProfiles, enabledModules, tenantName);
  const profileSet = new Set<string>(profiles);
  const terms = resolveCrmTerms(profiles, terminologyOverrides);
  const profileLabelById: Record<string, string> = {
    'crm-signals': terms.signal,
    'crm-pipeline': terms.deal,
    'crm-quotations': terms.quote,
    'crm-follow-ups': terms.followUp,
    'crm-recovery': terms.recovery,
    'crm-accounts': terms.account,
    'crm-contacts': terms.contact,
    'crm-incentives': terms.incentive,
  };

  return navigation.map((cat) => {
    if (cat.id !== 'crm' && cat.id !== 'rm') return cat;
    const labelById: Record<string, string> = cat.id === 'rm'
      ? {
        'crm-signals': 'Leads',
        'crm-pipeline': 'Pipeline',
        'crm-quotations': 'Customer Quotation',
        'crm-follow-ups': 'Follow-ups',
        'crm-recovery': 'Recovery',
        'crm-accounts': 'Customers',
        'crm-contacts': 'Contacts',
        'crm-incentives': 'Incentives',
      }
      : profileLabelById;
    const items = cat.items
      .filter((item) => !item.crmProfiles || item.crmProfiles.some((profile) => profileSet.has(profile)))
      .map((item) => {
        const label = labelById[item.id];
        return label ? { ...item, label, labelPlural: label } : item;
      });
    return { ...cat, items };
  });
}

function mergeNavigationItems(
  primaryItems: NavigationItem[],
  secondaryItems: NavigationItem[],
): NavigationItem[] {
  const mergedItems: NavigationItem[] = [];
  const seenKeys = new Set<string>();

  for (const item of [...primaryItems, ...secondaryItems]) {
    const key = (item.route || item.slug || item.id).trim().toLowerCase();
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    mergedItems.push(item);
  }

  return mergedItems;
}

function applyAccountingNavigationGovernance(
  navigation: NavigationCategory[],
): NavigationCategory[] {
  const acCategory = navigation.find((category) => (
    category.id !== 'finance' && normalizeNavigationCategoryId(category.id) === 'ac'
  ));
  const financeCategory = navigation.find((category) => category.id === 'finance');

  if (!financeCategory) return navigation;

  if (acCategory) {
    return navigation
      .map((category) => {
        if (category !== acCategory) return category;
        return {
          ...category,
          label: category.label ?? categoryLabelFor('ac'),
          labelKey: category.labelKey ?? 'category.ac',
          icon: category.icon ?? categoryIconFor('ac'),
          zone: category.zone ?? categoryZoneFor('ac'),
          order: Math.min(category.order, financeCategory.order),
          requiredModule: category.requiredModule ?? categoryModuleFor('ac'),
          requiredPermission: category.requiredPermission ?? categoryPermissionFor('ac'),
          items: mergeNavigationItems(category.items, financeCategory.items),
        };
      })
      .filter((category) => category.id !== 'finance')
      .sort((a, b) => a.order - b.order);
  }

  return navigation
    .map((category) => {
      if (category.id !== 'finance') return category;
      return {
        ...category,
        label: 'Accounting',
        labelKey: 'category.ac',
        icon: category.icon ?? categoryIconFor('ac'),
        zone: category.zone ?? categoryZoneFor('ac'),
        order: Math.min(category.order, categoryOrderFor('ac')),
        requiredModule: category.requiredModule ?? categoryModuleFor('ac'),
        requiredPermission: category.requiredPermission ?? categoryPermissionFor('ac'),
      };
    })
    .sort((a, b) => a.order - b.order);
}

function isLchTenantProfile(tenantSlug?: string, tenantName?: string): boolean {
  const slug = (tenantSlug ?? '').trim().toLowerCase();
  const name = (tenantName ?? '').trim().toLowerCase();
  return slug === 'lch-logistics'
    || slug === 'otc-asia'
    || slug === 'otcasia'
    || name.includes('lch')
    || name.includes('otc asia');
}

function isWascoTenantProfile(tenantSlug?: string, tenantName?: string): boolean {
  const slug = (tenantSlug ?? '').trim().toLowerCase();
  const name = (tenantName ?? '').trim().toLowerCase();
  return slug === 'wasco-malaysia'
    || name.includes('wasco');
}

function createLchCategory(
  source: NavigationCategory | undefined,
  override: {
    id: string;
    label: string;
    icon: string;
    order: number;
    zone: NavigationCategory['zone'];
    requiredModule?: string;
    requiredPermission?: string;
    items: NavigationItem[];
  },
): NavigationCategory {
  return {
    id: override.id,
    label: override.label,
    labelKey: `lch.nav.${override.id}`,
    icon: override.icon,
    order: override.order,
    zone: override.zone,
    requiredModule: override.requiredModule,
    requiredPermission: override.requiredPermission,
    dynamicRuntime: source?.dynamicRuntime,
    items: override.items,
  };
}

function applyLchNavigationProfile(
  navigation: NavigationCategory[],
  tenantSlug?: string,
  tenantName?: string,
): NavigationCategory[] {
  if (!isLchTenantProfile(tenantSlug, tenantName)) return navigation;

  const byId = new Map(navigation.map((category) => [category.id, category] as const));
  const home = byId.get('home');
  const business = byId.get('business');
  const rm = byId.get('rm') ?? byId.get('crm');
  const ai = byId.get('ai');
  const ct = byId.get('ct');
  const tm = byId.get('tm-runtime');
  const wg = byId.get('wg');
  const hrm = byId.get('workforce') ?? byId.get('hrm');
  const ac = byId.get('ac') ?? byId.get('finance');
  const settings = byId.get('settings');

  const fallbackHomePool = MODULE_NAV_ITEMS.home ?? [];
  const homeSourceItems = home?.items?.length ? home.items : fallbackHomePool;
  const curatedHomeItems = homeSourceItems.filter((item) => (
    item.route === '/'
    || item.route === '/today'
  ));
  const homeItems = curatedHomeItems.length > 0 ? curatedHomeItems : homeSourceItems;
  const omItems = business?.items?.length ? business.items : (MODULE_NAV_ITEMS.business ?? []);
  const rmItems = (rm?.items?.length ? rm.items : (MODULE_NAV_ITEMS.rm ?? []))
    .filter((item) => item.route !== '/rm/today');
  const bmItems = mergeNavigationItems(omItems, rmItems);
  const chatItems: NavigationItem[] = ai ? [{
    id: 'ai-chat-global',
    slug: 'ai-chat',
    label: 'AI Chat',
    labelPlural: 'AI Chat',
    icon: 'bot',
    route: '/chat',
    group: 'tools',
    requiredModule: 'AI',
  }] : [];

  const compactNavigation = [
    homeItems.length > 0 ? createLchCategory(home, {
      id: 'home',
      label: 'Home',
      icon: 'home',
      order: 1,
      zone: 'core',
      items: homeItems,
    }) : null,
    bmItems.length > 0 ? createLchCategory(business ?? rm, {
      id: 'business',
      label: 'BM',
      icon: business?.icon ?? rm?.icon ?? 'briefcase-business',
      order: 2,
      zone: 'core',
      requiredModule: undefined,
      requiredPermission: business?.requiredPermission ?? rm?.requiredPermission,
      items: bmItems,
    }) : null,
    ai ? createLchCategory(ai, {
      id: 'ai',
      label: 'AI',
      icon: ai.icon ?? 'zap',
      order: 3,
      zone: 'core',
      requiredModule: ai.requiredModule,
      requiredPermission: ai.requiredPermission,
      items: ai.items,
    }) : null,
    ct ? createLchCategory(ct, {
      id: 'ct',
      label: 'CT',
      icon: ct.icon ?? 'navigation',
      order: 10,
      zone: 'verticals',
      requiredModule: ct.requiredModule,
      requiredPermission: ct.requiredPermission,
      items: ct.items,
    }) : null,
    tm ? createLchCategory(tm, {
      id: 'tm-runtime',
      label: 'TM',
      icon: tm.icon ?? 'workflow',
      order: 11,
      zone: 'verticals',
      requiredModule: tm.requiredModule,
      requiredPermission: tm.requiredPermission,
      items: tm.items,
    }) : null,
    wg ? createLchCategory(wg, {
      id: 'wg',
      label: 'WG',
      icon: wg.icon ?? 'warehouse',
      order: 12,
      zone: 'verticals',
      requiredModule: wg.requiredModule,
      requiredPermission: wg.requiredPermission,
      items: wg.items,
    }) : null,
    hrm ? createLchCategory(hrm, {
      id: hrm.id,
      label: 'HRM',
      icon: hrm.icon ?? 'users-round',
      order: 13,
      zone: 'support',
      requiredModule: hrm.requiredModule,
      requiredPermission: hrm.requiredPermission,
      items: hrm.items,
    }) : null,
    ac ? createLchCategory(ac, {
      id: ac.id,
      label: 'AC',
      icon: ac.icon ?? 'banknote',
      order: 20,
      zone: 'support',
      requiredModule: ac.requiredModule,
      requiredPermission: ac.requiredPermission,
      items: ac.items,
    }) : null,
    settings ? createLchCategory(settings, {
      id: 'settings',
      label: 'SET',
      icon: settings.icon ?? 'settings',
      order: 30,
      zone: 'admin',
      requiredModule: settings.requiredModule,
      requiredPermission: settings.requiredPermission,
      items: settings.items,
    }) : null,
    chatItems.length > 0 ? createLchCategory(ai, {
      id: 'ai-chat',
      label: 'AI Chat',
      icon: 'bot',
      order: 31,
      zone: 'core',
      requiredModule: 'AI',
      requiredPermission: ai?.requiredPermission ?? 'ai:read',
      items: chatItems,
    }) : null,
  ].filter((category): category is NavigationCategory => {
    if (!category) return false;
    return category.items.length > 0 || Boolean(category.dynamicRuntime);
  });

  return compactNavigation;
}

function applyWascoNavigationProfile(
  navigation: NavigationCategory[],
  tenantSlug?: string,
  tenantName?: string,
): NavigationCategory[] {
  if (!isWascoTenantProfile(tenantSlug, tenantName)) return navigation;
  const hiddenCategoryIds = new Set(['home', 'business', 'crm', 'rm', 'builder', 'ai', 'ai-chat']);
  return navigation
    .filter((category) => !hiddenCategoryIds.has(category.id))
    .map((category) => {
      if (category.id !== 'wp') return category;
      const normalizedItems = [...category.items]
        .map((item) => {
          const isWpOverview = item.id === 'wp-overview'
            || item.slug === 'overview'
            || item.label?.toLowerCase() === 'overview';
          if (!isWpOverview) return item;
          return {
            ...item,
            route: '/wp',
          };
        })
        .sort((a, b) => {
          const aOverview = a.id === 'wp-overview' || a.slug === 'overview';
          const bOverview = b.id === 'wp-overview' || b.slug === 'overview';
          if (aOverview === bOverview) return 0;
          return aOverview ? -1 : 1;
        });

      return {
        ...category,
        items: normalizedItems,
      };
    });
}

function syncNavigationWithModules(
  navigation: NavigationCategory[],
  fallbackNavigation: NavigationCategory[],
  enabledModules?: string[],
  navigationPolicy?: NavigationPolicyConfig,
): NavigationCategory[] {
  const enabledModuleSet = buildEnabledModuleSet(enabledModules);
  const fallbackByCategory = new Map(fallbackNavigation.map((category) => [category.id, category] as const));

  const normalizedPrimary = navigation
    .map((cat) => ({
      ...cat,
      items: normalizeNavigationItems(
        cat.id,
        cat.items.filter((item) => canShowForModule(item.requiredModule, enabledModuleSet)),
        navigationPolicy,
      ),
    }))
    .map((cat) => {
      const fallbackCategory = fallbackByCategory.get(cat.id);
      if (!fallbackCategory) return cat;
      const mergedItems = [...cat.items];
      const existingKeys = new Set(
        mergedItems.flatMap((item) => navItemKeys(item)),
      );
      const fallbackItems = normalizeNavigationItems(
        fallbackCategory.id,
        fallbackCategory.items.filter((item) => canShowForModule(item.requiredModule, enabledModuleSet)),
        navigationPolicy,
      );
      for (const item of fallbackItems) {
        if (navItemKeys(item).some((key) => existingKeys.has(key))) continue;
        mergedItems.push(item);
      }
      return {
        ...cat,
        items: mergedItems,
      };
    })
    .filter((cat) => !HIDDEN_DYNAMIC_CATEGORY_IDS.has(cat.id))
    .filter((cat) => !shouldHideNavCategory(cat.id, navigationPolicy))
    .filter((cat) => cat.items.length > 0 || Boolean(cat.dynamicRuntime))
    .filter((cat) => canShowForModule(cat.requiredModule, enabledModuleSet));

  if (enabledModuleSet.has('AI') && !normalizedPrimary.some((cat) => cat.id === 'ai')) {
    const aiBinding = resolvedDynamicRuntimeBindingForCategory('ai');
    if (aiBinding) {
      const aiCategory: NavigationCategory = {
        id: 'ai',
        label: 'AI',
        icon: aiBinding.categoryIcon ?? 'bot',
        zone: aiBinding.categoryZone ?? 'core',
        order: 20,
        requiredPermission: '*',
        requiredModule: 'AI',
        dynamicRuntime: aiBinding,
        items: [],
      };
      const insertIndex = normalizedPrimary.findIndex((cat) => cat.id === 'ct' || cat.zone === 'verticals');
      if (insertIndex >= 0) normalizedPrimary.splice(insertIndex, 0, aiCategory);
      else normalizedPrimary.push(aiCategory);
    }
  }

  return normalizedPrimary.sort((a, b) => a.order - b.order);
}

export function mergeApiConfig(
  fallback: LayoutConfig,
  apiConfig: ConfigApiResponse['config'],
  enabledModules?: string[],
  _role?: string,
): LayoutConfig {
  // Accept both canonical (features/languages/themes) and legacy (featureFlags/languageOptions/themeOptions) field names
  const apiFeatures = apiConfig.features ?? (apiConfig as any).featureFlags as Partial<FeatureFlags> | undefined;
  const apiLanguages = apiConfig.languages ?? (apiConfig as any).languageOptions as LanguageOption[] | undefined;
  const apiThemes = apiConfig.themes ?? (apiConfig as any).themeOptions as ThemeOption[] | undefined;
  const navigationPolicy = apiConfig.navigationPolicy ?? fallback.navigationPolicy;

  // Navigation is server-only: when the API does not return menu categories,
  // the shell renders no menu instead of generating a frontend dummy tree.
  let mergedNavigation: NavigationCategory[];
  if (apiConfig.navigation) {
    const enriched = enrichNavigation(apiConfig.navigation, navigationPolicy);
    mergedNavigation = syncNavigationWithModules(enriched, fallback.navigation, enabledModules, navigationPolicy);
  } else {
    mergedNavigation = syncNavigationWithModules(
      fallback.navigation,
      fallback.navigation,
      enabledModules,
      navigationPolicy,
    );
  }

  const mergedPwaNavigation = apiConfig.pwaNavigation
    ? (() => {
        const enriched = enrichNavigation(apiConfig.pwaNavigation.navigation, navigationPolicy);
        return {
          ...apiConfig.pwaNavigation,
          navigation: syncNavigationWithModules(
            enriched,
            fallback.navigation,
            enabledModules,
            navigationPolicy,
          ),
        };
      })()
    : undefined;

  const mergedExtensionNavigation = apiConfig.extensionNavigation
    ? (() => {
        const enriched = enrichNavigation(apiConfig.extensionNavigation.navigation, navigationPolicy);
        return {
          ...apiConfig.extensionNavigation,
          navigation: syncNavigationWithModules(
            enriched,
            fallback.navigation,
            enabledModules,
            navigationPolicy,
          ),
        };
      })()
    : undefined;

  return {
    locale: apiConfig.locale ?? fallback.locale,
    navigation: mergedNavigation,
    dashboard: apiConfig.dashboard ?? fallback.dashboard,
    branding: apiConfig.branding ?? fallback.branding,
    features: {
      ...fallback.features,
      ...(apiFeatures ?? {}),
      userMenuItems: apiFeatures?.userMenuItems ?? fallback.features.userMenuItems,
    },
    accentOptions: apiConfig.accentOptions ?? fallback.accentOptions,
    languages: apiLanguages ?? fallback.languages,
    themes: apiThemes ?? fallback.themes,
    categoryOrder: apiConfig.categoryOrder ?? fallback.categoryOrder,
    categoryIcons: apiConfig.categoryIcons ?? fallback.categoryIcons,
    notificationPollMs: apiConfig.notificationPollMs ?? fallback.notificationPollMs,
    realtimeStatusAggregator: apiConfig.realtimeStatusAggregator
      ? {
          ...fallback.realtimeStatusAggregator,
          ...apiConfig.realtimeStatusAggregator,
          enabled: apiConfig.realtimeStatusAggregator.enabled ?? fallback.realtimeStatusAggregator?.enabled ?? false,
          pollMs: apiConfig.realtimeStatusAggregator.pollMs ?? fallback.realtimeStatusAggregator?.pollMs ?? 30_000,
          snapshotEndpoint: apiConfig.realtimeStatusAggregator.snapshotEndpoint ?? fallback.realtimeStatusAggregator?.snapshotEndpoint ?? '',
          title: apiConfig.realtimeStatusAggregator.title ?? fallback.realtimeStatusAggregator?.title ?? 'Realtime Status',
          setup: {
            ...fallback.realtimeStatusAggregator?.setup,
            ...apiConfig.realtimeStatusAggregator.setup,
            enabled: apiConfig.realtimeStatusAggregator.setup?.enabled ?? fallback.realtimeStatusAggregator?.setup?.enabled ?? false,
            allowedRoles: apiConfig.realtimeStatusAggregator.setup?.allowedRoles ?? fallback.realtimeStatusAggregator?.setup?.allowedRoles ?? [],
          },
          summaryMetrics: apiConfig.realtimeStatusAggregator.summaryMetrics ?? fallback.realtimeStatusAggregator?.summaryMetrics ?? [],
          tabs: apiConfig.realtimeStatusAggregator.tabs ?? fallback.realtimeStatusAggregator?.tabs ?? [],
        }
      : fallback.realtimeStatusAggregator,
    landingRoute: apiConfig.landingRoute ?? fallback.landingRoute,
    navMode: apiConfig.navMode ?? fallback.navMode,
    mobileNavStyle: apiConfig.mobileNavStyle ?? fallback.mobileNavStyle,
    rpaActions: apiConfig.rpaActions ?? fallback.rpaActions,
    pwaNavigation: mergedPwaNavigation,
    extensionNavigation: mergedExtensionNavigation,
    navigationPolicy,
    strictNavigation: apiConfig.strictNavigation ?? fallback.strictNavigation,
    warehouseProfile: apiConfig.warehouseProfile ?? fallback.warehouseProfile,
    warehouseProfileTemplateCode: apiConfig.warehouseProfileTemplateCode ?? fallback.warehouseProfileTemplateCode,
    warehouseTemplateAssignments: apiConfig.warehouseTemplateAssignments ?? fallback.warehouseTemplateAssignments,
    warehouseNavigationPolicy: apiConfig.warehouseNavigationPolicy ?? fallback.warehouseNavigationPolicy,
    crmProfiles: apiConfig.crmProfiles ?? fallback.crmProfiles,
    crmTerminology: apiConfig.crmTerminology ?? fallback.crmTerminology,
    isDefault: false,
  };
}

export function normalizeConfigForTenant(
  config: LayoutConfig,
  enabledModules?: string[],
  fallbackConfig?: LayoutConfig,
  tenantSlug?: string,
): LayoutConfig {
  const navigationPolicy = config.navigationPolicy ?? fallbackConfig?.navigationPolicy;
  const fallbackNavigation = config.navigation;
  let navigation = syncNavigationWithModules(
    config.navigation,
    fallbackNavigation,
    enabledModules,
    navigationPolicy,
  );
  navigation = applyCrmProfileToNavigation(
    navigation,
    config.crmProfiles,
    enabledModules,
    config.crmTerminology,
    config.branding.appName || config.branding.logoText,
  );
  navigation = applyWarehouseNavigationGovernance(navigation, {
    enabledModules,
    warehouseProfileTemplateCode: config.warehouseProfileTemplateCode,
    policy: config.warehouseNavigationPolicy,
  });
  navigation = applyAccountingNavigationGovernance(navigation);
  navigation = applyLchNavigationProfile(
    navigation,
    tenantSlug,
    config.branding.appName || config.branding.logoText,
  );
  navigation = applyWascoNavigationProfile(
    navigation,
    tenantSlug,
    config.branding.appName || config.branding.logoText,
  );

  const hiddenCategoryIds = new Set(navigation.map((cat) => cat.id));
  const quickNavCategories = (config.dashboard.quickNavCategories ?? []).filter((catId) => hiddenCategoryIds.has(catId));

  const pwaNavigation = config.pwaNavigation
    ? {
        ...config.pwaNavigation,
        navigation: syncNavigationWithModules(
          config.pwaNavigation.navigation,
          config.pwaNavigation.navigation,
          enabledModules,
          navigationPolicy,
        ),
      }
    : config.pwaNavigation;
  if (pwaNavigation) {
    pwaNavigation.navigation = applyCrmProfileToNavigation(
      pwaNavigation.navigation,
      config.crmProfiles,
      enabledModules,
      config.crmTerminology,
      config.branding.appName || config.branding.logoText,
    );
    pwaNavigation.navigation = applyWarehouseNavigationGovernance(pwaNavigation.navigation, {
      enabledModules,
      warehouseProfileTemplateCode: config.warehouseProfileTemplateCode,
      policy: config.warehouseNavigationPolicy,
    });
    pwaNavigation.navigation = applyAccountingNavigationGovernance(pwaNavigation.navigation);
    pwaNavigation.navigation = applyWascoNavigationProfile(
      pwaNavigation.navigation,
      tenantSlug,
      config.branding.appName || config.branding.logoText,
    );
  }

  const extensionNavigation = config.extensionNavigation
    ? {
        ...config.extensionNavigation,
        navigation: syncNavigationWithModules(
          config.extensionNavigation.navigation,
          config.extensionNavigation.navigation,
          enabledModules,
          navigationPolicy,
        ),
      }
    : config.extensionNavigation;
  if (extensionNavigation) {
    extensionNavigation.navigation = applyCrmProfileToNavigation(
      extensionNavigation.navigation,
      config.crmProfiles,
      enabledModules,
      config.crmTerminology,
      config.branding.appName || config.branding.logoText,
    );
    extensionNavigation.navigation = applyWarehouseNavigationGovernance(extensionNavigation.navigation, {
      enabledModules,
      warehouseProfileTemplateCode: config.warehouseProfileTemplateCode,
      policy: config.warehouseNavigationPolicy,
    });
    extensionNavigation.navigation = applyAccountingNavigationGovernance(extensionNavigation.navigation);
    extensionNavigation.navigation = applyWascoNavigationProfile(
      extensionNavigation.navigation,
      tenantSlug,
      config.branding.appName || config.branding.logoText,
    );
  }

  return {
    ...config,
    crmProfiles: resolveCrmProfilesForConfig(
      config.crmProfiles,
      enabledModules,
      config.branding.appName || config.branding.logoText,
    ),
    navigation,
    pwaNavigation,
    extensionNavigation,
    dashboard: {
      ...config.dashboard,
      quickNavCategories,
    },
  };
}

function normalizeConfigForRole(
  config: LayoutConfig,
  role?: string,
): LayoutConfig {
  if (role !== 'platform_admin') return config;

  const allowedPlatformIds = new Set(DEFAULT_CATEGORY_ORDER.filter((id) => id.startsWith('cc-')));
  const platformNavigation = config.navigation.filter((cat) => allowedPlatformIds.has(cat.id));
  const defaultQuickNav = ['cc-command', 'cc-revenue', 'cc-delivery', 'cc-ceo', 'cc-tenant-admin', 'cc-systems'];
  const allowedQuickNav = new Set(platformNavigation.map((cat) => cat.id));
  const quickNavCategories = (config.dashboard.quickNavCategories ?? [])
    .filter((catId) => allowedQuickNav.has(catId));

  return {
    ...config,
    navigation: platformNavigation,
    dashboard: {
      ...config.dashboard,
      kpiWidgets: [],
      widgets: [],
      quickNavCategories: quickNavCategories.length > 0
        ? quickNavCategories
        : defaultQuickNav.filter((catId) => allowedQuickNav.has(catId)),
    },
    landingRoute: '/admin/platform-dynamic',
  };
}

// ─── Hook ──────────────────────────────────────────

interface UseLayoutConfigResult {
  config: LayoutConfig | null;
  loading: boolean;
  error: string | null;
  entities: EntityMeta[];
  /** Force reload config + entities */
  refresh: () => void;
}

// Module-level cache to avoid re-fetching across components
let cachedConfig: LayoutConfig | null = null;
let cachedEntities: EntityMeta[] = [];
let cacheTimestamp = 0;
let cachedScopeKey = '';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const PERSISTENT_LAYOUT_CACHE_PREFIX = 'boox.layout-config.v6.cc-admin-sdk-nav';

interface PersistentLayoutCache {
  scopeKey: string;
  timestamp: number;
  config: LayoutConfig;
  entities: EntityMeta[];
}

function canUsePersistentLayoutCache(scopeKey: string): boolean {
  return scopeKey.split(':').some(Boolean);
}

function getLayoutCacheStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function persistentLayoutCacheKey(scopeKey: string): string {
  return `${PERSISTENT_LAYOUT_CACHE_PREFIX}:${encodeURIComponent(scopeKey)}`;
}

function readPersistentLayoutCache(scopeKey: string): PersistentLayoutCache | null {
  if (!canUsePersistentLayoutCache(scopeKey)) return null;
  const storage = getLayoutCacheStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(persistentLayoutCacheKey(scopeKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistentLayoutCache>;
    if (
      parsed.scopeKey !== scopeKey
      || typeof parsed.timestamp !== 'number'
      || !parsed.config
      || !Array.isArray(parsed.entities)
    ) {
      return null;
    }
    if (Date.now() - parsed.timestamp >= CACHE_TTL_MS) {
      storage.removeItem(persistentLayoutCacheKey(scopeKey));
      return null;
    }
    return parsed as PersistentLayoutCache;
  } catch {
    return null;
  }
}

function writePersistentLayoutCache(scopeKey: string, config: LayoutConfig, entities: EntityMeta[]): void {
  if (!canUsePersistentLayoutCache(scopeKey)) return;
  const storage = getLayoutCacheStorage();
  if (!storage) return;

  try {
    const payload: PersistentLayoutCache = {
      scopeKey,
      timestamp: Date.now(),
      config,
      entities,
    };
    storage.setItem(persistentLayoutCacheKey(scopeKey), JSON.stringify(payload));
  } catch {
    // sessionStorage may be full or disabled. The in-memory cache still works.
  }
}

function removePersistentLayoutCache(scopeKey: string): void {
  const storage = getLayoutCacheStorage();
  if (!storage || !canUsePersistentLayoutCache(scopeKey)) return;
  try {
    storage.removeItem(persistentLayoutCacheKey(scopeKey));
  } catch {
    // ignore
  }
}

export function useLayoutConfig(): UseLayoutConfigResult {
  const { user } = useAuth();
  const tenantId = user?.tenantId ?? '';
  const userId = user?.id ?? '';
  const role = user?.role ?? '';
  const moduleSignature = (user?.enabledModules ?? []).slice().sort().join(',');
  const scopeKey = `${tenantId}:${userId}:${role}:${moduleSignature}`;
  const hasFreshScopeCache = Boolean(
    cachedConfig
    && cachedScopeKey === scopeKey
    && Date.now() - cacheTimestamp < CACHE_TTL_MS,
  );
  const persistentScopeCache = hasFreshScopeCache ? null : readPersistentLayoutCache(scopeKey);
  const initialConfig = hasFreshScopeCache ? cachedConfig : persistentScopeCache?.config ?? null;
  const initialEntities = hasFreshScopeCache ? cachedEntities : persistentScopeCache?.entities ?? [];
  const hasInitialConfig = Boolean(initialConfig);
  const [config, setConfig] = useState<LayoutConfig | null>(initialConfig);
  const [entities, setEntities] = useState<EntityMeta[]>(initialEntities);
  const [loading, setLoading] = useState(!hasInitialConfig);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    cachedConfig = null;
    cachedScopeKey = '';
    cacheTimestamp = 0;
    removePersistentLayoutCache(scopeKey);
    setRefreshKey((k) => k + 1);
  }, [scopeKey]);

  useEffect(() => {
    // Use cache if fresh
    if (cachedConfig && cachedScopeKey === scopeKey && Date.now() - cacheTimestamp < CACHE_TTL_MS) {
      setConfig(cachedConfig);
      setEntities(cachedEntities);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(!hasInitialConfig);
    setError(null);

    // Fetch entities for entity catalog consumers. Navigation must come from
    // the server config only; entities never synthesize menu items here.
    const entitiesP = api.get<EntityMeta[]>('/entities').catch(() => [] as EntityMeta[]);

    // Fetch config from API
    const configP = api
      .get<ConfigApiResponse>('/config/current')
      .catch(() => null);

    const runtimeRegistryP = primeRuntimeModuleRegistry({ surface: 'web' });

    Promise.all([entitiesP, configP, runtimeRegistryP]).then(([allEntities, apiRes]) => {
      if (cancelled) return;

      const compatibilityFallback = buildFallbackConfig(allEntities);
      const serverOnlyFallback = buildServerOnlyEmptyConfig();

      // For platform_admin: always include the synthetic 'platform:admin' module so that
      // cc-* nav categories (requiredModule: 'platform:admin') survive syncNavigationWithModules.
      const effectiveModules = user?.role === 'platform_admin'
        ? [...(user?.enabledModules ?? []), 'platform:admin']
        : user?.enabledModules;

      // Merge with API config if available (even default configs may carry
      // nav overrides from platform_settings, so always merge when config exists)
      let finalConfig: LayoutConfig;
      if (apiRes?.config) {
        finalConfig = mergeApiConfig(compatibilityFallback, apiRes.config, effectiveModules, user?.role);
      } else {
        finalConfig = normalizeConfigForTenant(serverOnlyFallback, effectiveModules, undefined, user?.tenantSlug);
      }

      finalConfig = normalizeConfigForTenant(finalConfig, effectiveModules, undefined, user?.tenantSlug);
      finalConfig = normalizeConfigForRole(finalConfig, user?.role);

      // Update cache
      cachedConfig = finalConfig;
      cachedEntities = allEntities;
      cachedScopeKey = scopeKey;
      cacheTimestamp = Date.now();
      writePersistentLayoutCache(scopeKey, finalConfig, allEntities);

      setConfig(finalConfig);
      setEntities(allEntities);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : 'Failed to load config');
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [refreshKey, scopeKey, tenantId, user?.enabledModules, hasInitialConfig]);

  return useMemo(
    () => ({ config, loading, error, entities, refresh }),
    [config, loading, error, entities, refresh],
  );
}

// ─── Context for sharing across component tree ─────

import { createContext, useContext } from 'react';

export interface LayoutConfigContextValue {
  config: LayoutConfig;
  entities: EntityMeta[];
  refresh: () => void;
}

export const LayoutConfigContext = createContext<LayoutConfigContextValue | null>(null);
const FALLBACK_LAYOUT_CONTEXT: LayoutConfigContextValue = {
  config: buildServerOnlyEmptyConfig(),
  entities: [],
  refresh: () => {},
};

/**
 * Use the layout config from the nearest Provider.
 * Falls back to an empty server-only config when a route renders outside
 * of LayoutConfigProvider. Menu items must still come from server config.
 */
export function useLayoutConfigContext(): LayoutConfigContextValue {
  const ctx = useContext(LayoutConfigContext);
  if (ctx) return ctx;
  return {
    config: cachedConfig ?? FALLBACK_LAYOUT_CONTEXT.config,
    entities: cachedEntities.length > 0 ? cachedEntities : FALLBACK_LAYOUT_CONTEXT.entities,
    refresh: FALLBACK_LAYOUT_CONTEXT.refresh,
  };
}
